/**
 * Writer-facing Docs / Notes / Cards names (#1025).
 * Joel lock 2026-09-09. Cards is reserved for a later feature.
 * Import these strings instead of scattering literals.
 *
 * Style-room names (Joel, 2026-10-24, option A): REACT_APP_STYLE_ROOM
 * off (the default, and live) keeps every string below exactly as written.
 * On, roomCopy swaps the keys in STYLE_ROOM_ON_COPY. Code names
 * (voice_*, StyleCardsPage) stay. A character's way of speaking stays
 * "voice" in the four places that mean that: the character Voice field,
 * "Whose voice?", "+ Develop Voice", and the "Character Voice" placeholder.
 */

/**
 * On when the build set REACT_APP_STYLE_ROOM to 1, true, on, or yes.
 * CRA bakes the value in at build time, so this is fixed for a deploy.
 */
export function styleRoomEnabled() {
  const raw = String(process.env.REACT_APP_STYLE_ROOM || '').trim().toLowerCase();
  return raw === '1' || raw === 'true' || raw === 'on' || raw === 'yes';
}

/** Phone chat: the one button that opens the composer's other controls. */
export const CHAT_TOOLS = 'Chat Tools';

/**
 * Writer-facing words for the style room. Keys match the exports that
 * call roomCopy. Absent keys keep today's words even when the switch is on.
 */
export const STYLE_ROOM_ON_COPY = {
  STYLE_NOTES: 'Styles',
  OPEN_STYLE_NOTES: 'Open Styles',
  LOADING_STYLE_NOTES: 'Loading Styles\u2026',
  LOAD_STYLE_NOTES_ERROR: 'Could not load Styles.',
  OPEN_BOOK_FOR_STYLE_NOTES: 'Open a book for Styles',
  STYLE_ACTION_FAILED: 'That style action failed.',
  SELECT_BOOK_FOR_STYLE_NOTE: 'Select a book to draft a style.',
  EXISTING_VOICES: 'Already in Styles \u2014 Skip this if it is a duplicate. Nothing merges on its own.',
  VOICE_LABEL: 'Style',
  VOICE_DRAFT: 'Style',
  VOICE_HEADING: 'Book style',
  NO_VOICE_YET: 'No style yet',
  NO_VOICE_ON_SCENE: 'This scene has no style yet.',
  VOICE_NAME: 'Style name',
  SAVED_VOICES: 'Your styles',
  NO_VOICES_IN_LIBRARY: 'No saved styles yet. Make one, then save it to your library.',
  POINT_SCENE_AT_VOICE: 'Use this style for this scene',
  POINT_CHAPTER_HINT: (
    'Pick a style for a chapter or scene here. It replaces the book style for that part. '
    + 'You write the scene in Write.'
  ),
  PICK_ANOTHER_VOICE: 'Pick another style',
  CHOOSE_A_VOICE: 'Choose a style',
  USE_THIS_VOICE: 'Use this style for this scene',
  SCENE_VOICE_REPLACES: (
    'A scene style replaces the book style for this scene only. Saving it and '
    + 'making it the book style are separate choices. Nothing is '
    + 'added in the background.'
  ),
  COPY_BOOK_VOICE_INTO_SCENE: 'Use the book style',
  MAKE_THIS_THE_BOOK_VOICE: 'Make this the book style',
  RULE_SITS_NEXT_TO_VOICE: 'Hard rules apply alongside the style. Turning one off keeps the sentence.',
  STYLE_RULES_VOICE_LINE: 'can turn it on or off. Styles live on Styles, in another',
  STORY_RULES_LEDE: (
    'A story rule is a hard rule, not a style. One plain sentence you '
    + 'edit. It can belong to the book, a person, or a scene, and you '
    + 'can turn it on or off. Styles live on Styles, in another '
    + 'room. This is not where you write the scene.'
  ),
  STORY_RULES_NAV_TIP: (
    'Add rules this story must always follow when you generate \u2014 what\u2019s true, and how the style should stay'
  ),
  STYLE_NOTES_NAV_TIP: 'Set the style for this book: how your scenes should sound when they\u2019re drafted.',
  STYLE_LEDE: (
    'A style is one instruction you can read and edit. Draft it from sample lines, '
    + 'how the sentences work, the words we use, and the tone. Facts about people and world '
    + 'stay on Book Notes. The POV character is set here, not as part of the style.'
  ),
  GENERATE_VOICE_FROM_CHARACTER_NOTE: 'Make a style from a Character note',
  GENERATE_VOICE_FROM_THEIR_CHARACTER_NOTE: 'Make a style from their Character note',
  GENERATE_VOICE_HINT: (
    'This book only. One style from who they are and how they come across. '
    + 'It isn\u2019t the book style until you make it one.'
  ),
  LOOKS_LIKE_EXISTING_VOICE: (
    'This looks like a style you already have. Update that one, or make a new one. Nothing merges on its own.'
  ),
  ACCEPT_VOICE_STYLE_BOOK: 'Accepting this saves a style. It won\u2019t become the book style unless you make it one.',
  ACCEPT_STORY_RULE_BOOK: (
    'Accepting this adds a Story Rule on this book. A hard rule that must stay true. It is not the book style.'
  ),
  FROM_DOCS_LEDE: (
    'Pick Docs already in the pile. Propose Character, Place, World, Logline, Theme, Conflict, Relationship, Subplot, Custom, Story Rules, and styles. You accept, edit, or Skip. Nothing is written until you say so.'
  ),
  STYLE_FILTER_BODY: 'Styles are how your scenes sound.',
  POV_DISTINCTION: (
    'Narrative POV is how the book is told. POV characters are who can carry a scene. '
    + 'Each scene has one POV character, or All. How it sounds is a style.'
  ),
  UNTITLED_VOICE: 'Untitled style',
  VOICE_SAVED_FOR_BOOK: 'Style saved. It isn\u2019t the book style unless you make it one.',
  NO_DEFAULT_VOICE_YET: 'This book has no book style yet.',
  REVIEW_THIS_VOICE: 'Check this style. Accept, edit, or reject. It won\u2019t become the book style on its own.',
  THIN_CHARACTER_NOTE: (
    'This Character note is thin. The draft says so and does not make up a whole new way of sounding. Accept, edit, or reject.'
  ),
  VOICE_INSTRUCTION_PLACEHOLDER: 'Write how you want your scenes to sound.',
  VOICE_TIED_TO_PERSON: 'Style for a person',
  NONE_BOOK_VOICE: 'None (book style)',
  RULE_ABOUT_VOICE: 'Rule about style',
  KEEP_THE_VOICE: 'Keep the style',
  VOICE_RULE_YIELDS: 'A rule that is itself a style yields unless you keep it.',
  GIVE_VOICE_ITS_TEXT: 'Give this style its text before you accept it.',
  WHERE_VOICE_LANDS: 'Where this style goes',
  VOICE_ALREADY_HERE: 'You already have this style',
  PARENT_VOICE_LINE: 'Started from the parent style',
  NO_VOICE_SAVED_YET: 'No style saved yet. Write the instruction, then Save instruction.',
  EXTRA_GUIDANCE_HINT: (
    'Write follows What Write will use, above. These notes stay saved '
    + 'with the style. They help when you draft a new instruction.'
  ),
};

function roomCopy(key, off) {
  if (!styleRoomEnabled()) return off;
  if (Object.prototype.hasOwnProperty.call(STYLE_ROOM_ON_COPY, key)) {
    return STYLE_ROOM_ON_COPY[key];
  }
  return off;
}

// ── Locked room names ────────────────────────────────────────────────

/** Uploaded reference files. Today's Notes upload pile. */
export const DOCS = 'Docs';
export const SERIES_DOCS = 'Series Docs';
export const BOOK_PLANNING_DOCS = 'Book Planning Docs';

/** Today's Cards family. */
export const BOOK_NOTES = 'Book Notes';
export const SERIES_NOTES = 'Series Notes';
export const STYLE_NOTES = roomCopy('STYLE_NOTES', 'Style Notes');

/** Reserved. Do not use for Docs or Notes. */
export const CARDS_RESERVED = 'Cards';

/** Writer-facing name of the outline grid. Routes and ids stay /plottrack. */
export const PLOT_TRACK = 'Plot Grid';
/** Room title on the grid. The grid itself stays Plot Grid. */
export const OUTLINE_ROOM_TITLE = 'Outline';
export const OUTLINE_ROOM_SUBTITLE = 'Plot Grid · your outline, scene by scene';

/** On-demand outline check. Peer to Generate. Not a chat. */
export const CHECK_OUTLINE = 'Check outline';
export const CHECK_OUTLINE_BUSY = 'Checking the outline…';
export const CHECK_OUTLINE_THIN = 'Not enough outline to check.';
export const CHECK_OUTLINE_CLEAR = 'Nothing stood out in this outline.';
export const CHECK_OUTLINE_FAILED = 'The outline check did not finish. Try again.';
export const CHECK_OUTLINE_WHERE = 'Where';
export const CHECK_OUTLINE_HINT = 'What to do';
export const CHECK_OUTLINE_HARD = 'Hard';
export const CHECK_OUTLINE_SOFT = 'Soft';

/** Wave 3 journey rail — ordinary words primary, product name secondary. */
export const DISCOVERY = 'Discovery';

// Step 0 (#2066). Draft for Joel. The question is the one spec key.
export const STEP0_QUESTION = 'Do you have an idea of what the story is about?';
export const STEP0_YES = 'Yes';
export const STEP0_NO = 'No';
export const STEP0_ANSWER_PROMPT = 'Type your idea below. A person, a place, a genre, or a theme is enough.';
export const TASKS = 'Tasks';
export const PLOT_WORKSHOP = 'Plot Workshop';
export const SCENE_WRITING = 'Scene Writing';
export const EXPLORE_YOUR_STORY = 'Explore your story';
export const REVIEW_TASKS = 'Review tasks';
export const SHAPE_THE_PLOT = 'Work the outline';
export const ARRANGE_SCENES = 'Outline';
export const DRAFT_PROSE = 'Draft prose';
export const BOOK_TOOLS = 'Book tools';
export const BOOK_TOOL_NAMES = 'Names';
export const BOOK_TOOL_RESEARCH = 'Research';
export const BOOK_TOOL_COMPENDIUM = 'Compendium';
export const BOOK_TOOL_STORY_PROGRESS = 'Story progress';
export const CLOSE_NAMES = 'Close Names';
export const CLOSE_RESEARCH = 'Close Research';
export const CLOSE_COMPENDIUM = 'Close Compendium';
export const CLOSE_STORY_PROGRESS = 'Close Story progress';
export const SUGGESTED_PATH_HINT = (
  'Suggested path — jump to any room. Nothing is locked.'
);
export const DRAFT_THIS_SCENE = 'Draft this scene';

// Suggested path (bookPathTip): progress, then the one next step.
// Once an outline exists, sequences that have scenes count as filled.
// Approve in Plot Focus still means the writer approved that sequence.
export const PATH_PROGRESS = ({
  approved = 0,
  sequences = 0,
  drafted = 0,
  scenes = 0,
  filled = null,
  hasOutline = false,
} = {}) => {
  const sequenceCount = hasOutline && filled != null ? filled : approved;
  const sequenceLine = sequences
    ? (hasOutline
      ? `${sequenceCount} of ${sequences} sequences have scenes`
      : `${approved} of ${sequences} sequences approved`)
    : '';
  const sceneLine = scenes ? `${drafted} of ${scenes} scenes drafted` : '';
  return [sequenceLine, sceneLine].filter(Boolean).join(' · ') + (
    sequenceLine || sceneLine ? '.' : ''
  );
};
/** Accepted count. Names the drafts still not accepted when any remain. */
export const PATH_ACCEPT_COUNTS = (accepted, unaccepted) => {
  const yes = Number(accepted) || 0;
  const no = Number(unaccepted) || 0;
  if (no > 0) return `${yes} accepted, ${no} not accepted yet.`;
  if (yes > 0) return `${yes} accepted.`;
  return '';
};
export const PATH_TALK_NEXT = (name) => `Next: work through "${name}"`;
export const PATH_DRAFT_NEXT = (name) => `Next: draft "${name}"`;
export const PATH_REVIEW_NEXT = (name) => `Next: review "${name}"`;
export const PATH_NEXT_SCENE = (name) => `Next scene: "${name}"`;
// The scene on screen is still the one to write. The following open scene
// is after this one, not the next scene the Outline names.
export const PATH_AFTER_THIS = (name) => `After this: "${name}"`;
// Run 43: from the last scene, "Next scene" named the first one.
export const PATH_EARLIER_SCENE = (name) => `Back to "${name}", not accepted yet`;
// Run 51: "Draft prose" reopened a finished scene. It now opens the next one not accepted yet.
// Run 51: the sequence showed only as a number ("6.4"), and the title was hidden in a fold.
export const SCENE_IN_SEQUENCE = (label, sequence) => (sequence ? `${label} · ${sequence}` : label);
export const RESUME_PAST_ACCEPTED = (done, next) => (
  `"${done}" is accepted, so this opens "${next}", the next scene not accepted yet.`
);
export const PATH_ALL_DRAFTED = 'Every scene has an accepted draft.';
export const GRAIN_SEQUENCE = 'Sequence';
export const UNTITLED_SEQUENCE = 'Untitled sequence';
export const DRAFT_PACK_TITLE = 'Bigger than one scene';
export const DRAFT_PACK_HINT = (
  'This column is bigger than one scene. Drafting writes the whole column as one scene.'
);
export const DRAFT_PACK_WARN = (
  'This column is bigger than one scene. Drafting now writes the whole column as one scene. '
  + 'Go back, or draft it as one scene anyway.'
);
export const DRAFT_PACK_ANYWAY = 'Draft as one scene';
export const WRITE_DRAFT = 'Write draft';
/** Phone Write: the one folded row under the draft. */
export const SCENE_SETTINGS = 'Scene settings';
export const REVIEW_CONTEXT = 'Review context';
export const INSTRUCTIONS_OPTIONAL = 'Notes for this draft (optional)';
/** #1340: empty means "write it as planned" — the server says so for the draft. */
/** #1341 — Your cast: the book's people and places from what the writer said. */
export const CAST_LABEL = 'Your cast';
export const CAST_LEDE =
  'Brainstormy can list the people and places from what you have said about this book, so every draft knows them. You choose which to keep.';
export const CAST_FIND = 'Find your cast';
export const CAST_NOT_NOW = 'Not now';
export const CAST_FINDING = 'Reading what you said…';
export const CAST_REVIEW_LEDE = 'From your chats. Untick anyone you do not want in Book Notes. You can edit every note afterwards.';
export const CAST_ADD = (count) => (count === 1 ? 'Add 1 to Book Notes' : `Add ${count} to Book Notes`);
export const CAST_ADDED = (count) => (count === 1
  ? 'Added 1 note. Drafts and your POV characters now know them.'
  : `Added ${count} notes. Drafts and your POV characters now know them.`);
export const CAST_NONE_FOUND = 'No named people or places in your chats yet. Name them in a chat, then try again.';
export const CAST_PEOPLE = 'Person';
export const CAST_PLACE = 'Place';

/**
 * #2069 cards under a reply. Joel approved this wording.
 * #2120: the count chip is gone. Cards stay open.
 * IDEA_KIND_THING, IDEA_KIND_EVENT,
 * IDEA_OFFER_SAVE_FAILED, IDEA_OFFER_DISMISS_FAILED.
 * Save uses Add to Book Notes. Dismiss uses Not now.
 */
export const IDEA_KIND_THING = 'Thing';
export const IDEA_KIND_EVENT = 'Event';
export const IDEA_OFFER_SAVE_FAILED = 'Could not save this note.';
export const IDEA_OFFER_DISMISS_FAILED = 'Could not dismiss this.';

/** Cold-start run 11: series books started with Series Notes people but no Book Notes. */
export const BRING_SERIES_PEOPLE_LABEL = 'From your series';
// Run 21: a note saved with Save to series notes is a Custom note, and the offer named only
// people, so it was never offered. It now offers any series note this book doesn't have.
export const BRING_SERIES_PEOPLE_LINK = 'Bring series notes into this book';
export const BRING_SERIES_PEOPLE_LEDE =
  'Your Series Notes have notes this book doesn\u2019t have yet. Bring the ones it needs into Book Notes, so its chats, outline and drafts use them.';
export const BRING_SERIES_PEOPLE_NOT_NOW = 'Not now';
export const DRAFT_USES_LABEL = 'What this draft will use';
export const DRAFT_USES_YES = 'Yes';
export const DRAFT_USES_NOT_YET = 'Not yet';
export const DRAFT_USES_NOTHING =
  'The draft knows nothing about your story yet, so it may invent names and places. Work it through in Explore your story or add people in Book Notes first.';
// Series: drafts of Book N skip note sentences that name Book N or a later book
// (services/book_scope.py). Shown in Draft uses and where Notes are written.
export const HELD_BACK_LABEL = 'Held back';
export const heldBackValue = (count, book) => (
  `${count} note sentence${count === 1 ? '' : 's'} naming Book ${book} or later`
);
export const HELD_BACK_SHOW = 'See what this draft skips';
export const HELD_BACK_HINT = (
  'In a series, a Note sentence that names a book ("Exposed in Book 1") waits for the next book\'s '
  + 'drafts, so an ending or a later secret doesn\'t show up early. Your Notes are not changed. '
  + 'If a sentence describes how things stand when this book starts, rewrite it without the book number.'
);
export const SERIES_NOTES_BOOK_TIP = (
  'Series tip: name the book for what happens in it ("Exposed in Book 1", "Revealed in Book 3"). '
  + 'Drafts of that book and earlier ones skip the sentence; later books use it. '
  + 'A note titled for a book ("Book 3: Calloway", "Book 1 to Book 2") waits for that book. '
  + 'For how things stand when a book starts, leave the book number out.'
);
export const WRITE_DRAFT_INCLUDES_CHECK =
  'Includes the draft and the check that runs after it.';
export const WRITE_DRAFT_GENDER_LEAD =
  'Choose a gender for each person in this scene who does not have one yet. It stays with the book.';
export const WRITE_DRAFT_GENDER_WOMAN = 'Woman';
export const WRITE_DRAFT_GENDER_MAN = 'Man';
export const WRITE_DRAFT_REPLACES_EDITED =
  'This replaces your edited draft with a new one.';
export const WRITE_DRAFT_KEEPS_HAND_EDITS =
  'You edited this scene by hand. A new draft starts from the scene plan, not from your edits.';
export const BRING_BACK_EDITED_DRAFT = 'Bring back my edited draft';
export const ACCEPT_INCLUDES_NOTE_CHECK = 'Includes the Note check.';
export const CHECK_BEFORE_ACCEPTING = 'Check before accepting';
export const CHECK_BEFORE_ACCEPTING_HINT =
  'A separate check compared this draft with Book Notes and earlier accepted scenes.';
export const CHECK_NO_DISAGREEMENTS = 'Nothing stood out in this scene.';
// Every draft, before Accept, whatever the check found (Joel, after run 38):
// the check misses slips, so it must never read as a guarantee.
export const CHECK_READ_IT_YOURSELF =
  'This check catches some slips, not all. Read the draft yourself before you accept: names, numbers, and who is he or she.';
export const CHECK_COULD_NOT_RUN =
  "This check couldn't run. Read the draft against your Notes and earlier scenes before you accept.";
export const CHECK_THIS_DRAFT = 'This draft';
export const CHECK_THAT_NOTE = 'That Note';
export const CHECK_THAT_SCENE = 'That scene';
export const DRAFT_NOTES_PLACEHOLDER =
  'Leave empty to write the scene as planned. Add anything this draft needs: whose POV, a moment to land, a tone.';
export const VIEWPOINT_LABEL = 'POV';
export const VOICE_LABEL = roomCopy('VOICE_LABEL', 'Voice');
export const VIEWPOINT_UNSET = 'Not set yet';
export const WHOSE_HEAD = 'POV character';
// Plot Grid's board filter: lists only people already set on a scene. Runs 15 and 17
// read it as the POV picker and could not choose the hero.
export const PT_POV_FILTER_LABEL = 'Show scenes for';
// When labels on Plot Grid scenes (spec: docs/plottrack/plot-track-time-people-and-canon-room-spec.md §1).
export const PT_WHEN_ROW_LABEL = 'When';
export const PT_WHEN_NONE = '—';
export const PT_WHEN_CREATE = 'Create new…';
export const PT_WHEN_NEW_PLACEHOLDER = 'Any words: 1952, Present day, Ilse’s story';
export const PT_WHEN_SAVE = 'Save';
export const PT_WHEN_CANCEL = 'Cancel';
export const PT_WHEN_SHOW = 'Show a timeline row';
// Grid first (Joel, 2026-10-15): the grid is the room; tools open beside it.
export const PT_TOOLBAR_LABEL = 'Outline tools';
export const PT_TOOL_BUILD = 'Build outline';
export const PT_TOOL_ARRANGE = 'Add & arrange';
export const PT_TOOL_UNPLACED = (count) => `${count} unplaced`;
export const PT_PANEL_CLOSE = 'Close';
export const PT_GRID_HOW = 'Each column is a scene, in story order. Each row follows one thread across the scenes. Click a cell to read or edit it.';
export const PT_EMPTY_HOW = 'Your outline will appear here as a grid: one column per scene, in story order, one row per thread. Build it from your chats, notes and Framework.';
export const EMPTY_GRID_LEAD = 'No scenes on your Plot Grid yet. How do you want to start?';
export const OUTLINE_EMPTY_WRITE_TITLE = 'Write it myself';
export const OUTLINE_EMPTY_WRITE_BODY = 'Add scenes one at a time, in any order. You can add a Framework later.';
export const OUTLINE_EMPTY_FRAMEWORK_TITLE = 'Use a Framework';
export const OUTLINE_EMPTY_FRAMEWORK_BODY = 'Pick a Framework to give your story its sequences based on your genre, and Brainstormy will use it to suggest what happens in each sequence of your outline.';
export const OUTLINE_MORE = 'Outline more';
export const OUTLINE_MORE_VISIBLE = 'More';
export const OUTLINE_VIEW = 'View';
export const OUTLINE_MORE_VIEW = 'View';
export const OUTLINE_VIEW_AS = 'Show as';
export const OUTLINE_VIEW_CARDS = 'Scene cards';
export const OUTLINE_VIEW_GRID = 'Plot Grid';
export const OUTLINE_GRID_SIZE = 'Size';
export const OUTLINE_GRID_SIZE_COMFORTABLE = 'Comfortable';
export const OUTLINE_GRID_SIZE_COMPACT = 'Compact';
export const OUTLINE_NEXT_WRITE = (number) => `Next: write scene ${number}`;
export const OUTLINE_PART = (number, name) => (name ? `Sequence ${number} · ${name}` : `Sequence ${number}`);
export const OUTLINE_NOT_PLANNED = 'not planned yet';
export const OUTLINE_PLAN_PART = 'Plan this sequence';
export const SUGGEST_SCENES_FOR = (part) => `Suggest scenes for ${part}`;
export const SUGGEST_SCENES_HINT = (part) => (
  `Brainstormy will suggest scenes for ${part} using what you said in chat and your Notes. `
  + 'You can edit or remove them before they go on your Plot Grid.'
);
export const SUGGEST_SCENES_NOTE_LABEL = 'Anything to steer it? (optional note)';
export const SUGGEST_SCENES = 'Suggest scenes';
export const SUGGEST_NOT_YET = 'Not yet';
export const OUTLINE_EXAMPLE = (text) => `Example: ${text}`;
export const OUTLINE_CARD_HINT = 'Click a scene to open it. On a card, ⋯ is Rename, Move, Start a chapter here, Delete, and Chat about this scene.';
export const OUTLINE_CHAPTER_OUTSIDE = (chapter) => (
  `Drag ${chapter} into a sequence, or leave it here.`
);
export const OUTLINE_MORE_POV = 'Point of view for the book';
export const OUTLINE_MORE_RULES = 'Story rules';
export const OUTLINE_MORE_CHECK = 'Find plot holes';
export const OUTLINE_MORE_GAPS = 'Suggest missing scenes';
export const OUTLINE_MORE_GAPS_ON = 'Suggest missing scenes is on for this book.';
export const OUTLINE_MORE_GAPS_FAILED = 'Could not suggest missing scenes. Try again.';
export const OUTLINE_MORE_SUGGEST = 'Suggest scenes';
export const IDEA_ROOM_TITLE = 'Idea';
export const TURN_INTO_SCENES = 'Turn this into scenes';
export const TURN_INTO_SCENES_HINT = 'Opens Outline. Suggested scenes stay off the Plot Grid until you approve them.';
export const IDEA_MORE = 'Idea more';
export const IDEA_MORE_VISIBLE = 'More';
export const IDEA_MORE_NEW = 'New chat';
export const IDEA_MORE_SUMMARY = 'Summary';

/** Direct controls under the composer on Idea and in every room chat. */
export const HELP_JUST_LISTEN = 'Mute';
export const HELP_EXPLAIN = 'Teach';
export const HELP_GET_STARTED = 'Get me started';
export const HELP_SMALL_HINT = 'Nudge';
export const IDEA_NEEDS_BOOK = 'Open a book before turning this chat into scenes.';
export const NOTES_ROOM_TITLE = 'Notes';
export const NOTE_FILTER_LABEL = 'Note types';
export const NOTES_FILTER_ALL = 'All';
export const STYLE_FILTER = 'Style';
export const STYLE_FILTER_BODY = roomCopy('STYLE_FILTER_BODY', 'Style notes are how the book sounds.');
export const BRAINSTORM_IN_CHAT = 'Brainstorm in chat';
export const ADD_THIS_TO_THE_NOTE = 'Add this to the note';
export const ADD_THIS_TO_NOTES = 'Add this to Notes';
export const ADDED_TO_THE_NOTE = 'Added to the note.';
export const ADDED_TO_NOTES = 'Added to Notes.';
export const ADD_TO_NOTE_NEEDS_REPLY = 'Wait for a reply, then add it.';
export const ADD_TO_NOTE_FAILED = 'Could not add that to the note.';
export const UPLOAD_A_DOC = 'Upload a doc';
export const ATTACHED_NOTE = (name) => `Attached: ${name} (note)`;
export const ATTACHED_DOC = (name) => `Attached: ${name}`;
export const CLEAR_ATTACHMENT = 'Remove attachment';
export const FRAMEWORK_CHANGE = 'Change';
export const FRAMEWORK_REMOVE = 'Remove';
export const FRAMEWORK_REMOVE_TITLE = 'Remove this Framework?';
export const FRAMEWORK_REMOVE_BODY = 'Your scenes stay on the Plot Grid. This only takes the Framework off the book.';
export const FRAMEWORK_REMOVE_CONFIRM = 'Remove Framework';
export const FRAMEWORK_REMOVE_KEEP = 'Keep it';
export const GENERATE_CANT_ESTIMATE = "Can't estimate this yet. This stays off until a cost is known.";
export const EMPTY_GRID_TOOLS_WAIT = 'Check outline, Check my sequences, and Add a main storyline open once a scene is on the grid.';
// Run 53: Generate names each scene's timeline on a two-timeline structure; the writer
// uses them in one click, or not.
export const PT_TIMELINE_OFFER = (parts) => `Generate marked each scene's timeline: ${parts}.`;
export const PT_TIMELINE_OFFER_PART = (label, count) => `${label} (${count} ${count === 1 ? 'scene' : 'scenes'})`;
export const PT_TIMELINE_USE = 'Use these timelines';
export const PT_TIMELINE_USING = 'Saving…';
export const PT_TIMELINE_HINT = 'They go on the timeline row, where you can change any scene.';
export const PT_TIMELINE_FAILED = 'Could not save the timelines. Try again.';
export const PT_WHEN_HIDE = 'Hide the timeline row';
export const PT_WHEN_MANAGE = 'Edit When labels';
export const PT_WHEN_FILTER_GROUP = 'When';
export const PT_WHEN_OFFER = (label) => `${label}?`;
export const PT_WHEN_OFFER_ARIA = (label) => `Use When \u201c${label}\u201d for this scene`;
export const PT_WHEN_SELECT_ARIA = (scene) => `When for ${scene}`;
export const PT_WHEN_RENAME_ARIA = (label) => `Rename When label ${label}`;
export const PT_WHEN_UP = 'Move up';
export const PT_WHEN_DOWN = 'Move down';
export const PT_WHEN_DELETE = 'Delete';
export const PT_WHEN_DELETE_NOTE = 'Deleting a label clears it from its scenes.';
// People named in each scene (spec §2). Offered from names in the plan and prose; the writer edits.
export const PT_PEOPLE_HEADING = 'Named in this scene';
export const PT_PEOPLE_EMPTY = 'No one from Book Notes is named here yet.';
export const PT_PEOPLE_ADD = '+ Add someone';
export const PT_PEOPLE_REMOVE_ARIA = (name) => `Remove ${name} from this scene`;
export const PT_PEOPLE_FILTER_GROUP = 'Scenes naming';
// Run 52: a person who is a POV character and is named in scenes showed twice as the
// same name; group labels don't show in a closed list, so each option says which it is.
export const PT_POV_FILTER_GROUP = 'POV character';
export const PT_POV_FILTER_PERSON = (name) => `${name} (POV)`;
export const PT_NAMING_FILTER_PERSON = (name) => `Naming ${name}`;
export const PT_PEOPLE_MORE = (count) => `+${count}`;
export const PT_PEOPLE_DRAFT_ROW = 'People in this scene';
// Column width (spec §3). Saved on the book.
export const PT_WIDTH_LABEL = 'Column width';
export const PT_WIDTH_OPTION = (width) => `${width} columns`;
export const PT_WIDTH_NARROW = 'Narrow';
export const PT_WIDTH_STANDARD = 'Standard';
export const PT_WIDTH_WIDE = 'Wide';
export const PT_WIDTH_RESET = 'Reset widths';
export const PT_WIDTH_HANDLE_ARIA = (scene) => `Width of ${scene}. Use the arrow keys to change it.`;
export const PT_POV_FILTER_ALL = 'All scenes';
export const PT_POV_FILTER_HINT = "To set a scene's POV character, open the scene.";
export const PERSON = 'Narrative POV';
export const PERSON_FOR_SCENE = 'Narrative POV for this scene';
export const PERSON_INHERITED = 'Inherited';
export const PERSON_FROM_BOOK = 'From the book';
export const PERSON_FROM_CHAPTER = 'From this chapter';
export const PERSON_NO_DEFAULT = 'No book default yet';
export const POV_DONT_KNOW = "Don't know yet";
export const POV_CAMERA = 'How many POV characters';
export const POV_CAMERA_ONE = 'One person';
export const POV_CAMERA_SEVERAL = 'Several people';
export const POV_VIEWPOINT = 'Default POV character';
export const POV_CAST = 'POV characters';
export const POV_CAST_EMPTY = 'Add people in Notes first.';
export const POV_SAVE = 'Save book POV';
export const POV_CHIP_UNSET = 'Narrative POV not set';
export const POV_CHIP_DEFERRED = "Narrative POV: Don't know yet — set when you're ready.";
export const POV_HELP_CLOSE = 'Close third = deep in one mind.';
export const POV_HELP_LIMITED = 'Limited third = still one mind per scene but a little more distance.';
export const POV_HELP_EXAMPLES = (
  'First + one person = 1st single. First + several = 1st multiple. '
  + 'Limited third + several = 3rd limited multiple. Omniscient = 3rd omniscient.'
);
export const POV_HELP_FIRST_ONE = '1st single';
export const POV_HELP_FIRST_SEVERAL = '1st multiple';
export const POV_HELP_LIMITED_ONE = '3rd limited single';
export const POV_HELP_LIMITED_SEVERAL = '3rd limited multiple';
export const POV_HELP_OMNISCIENT = '3rd omniscient';
export const POV_OUTSIDE_CAST = 'Not one of your POV characters. You can still use them on this scene.';
export const POV_DISTINCTION = roomCopy('POV_DISTINCTION', (
  'Narrative POV is how the book is told. POV characters are who can carry a scene. '
  + 'Each scene has one POV character, or All. Voice is a Style Note.'
));
export const POV_STRIP_LEDE = 'How is this book told?';
export const PERSON_SAVE_FAILED = 'Could not save the narrative POV.';
export const PERSON_BOOK_DEFAULT_SET = (label) => (
  `${label || 'This'} is now the book's narrative POV, so every scene starts with it. Any scene can still differ.`
);
export const WHO_WE_ARE_WITH = 'POV character';
export const WHOSE_HEAD_NOT_SET = 'Not set';
// Run 51: while a scene loads, say so instead of showing the last scene's person.
export const WHOSE_HEAD_LOADING = 'Loading…';
export const WHOSE_HEAD_ALL = 'All';
export const ADD_PEOPLE_IN_NOTES = 'Add people in Notes';
// Run 43: the writer had to leave the scene to make Kip before picking him.
export const ADD_POV_PERSON = 'Add a person';
export const ADD_POV_PERSON_BUTTON = 'Add and use';
export const ADD_POV_PERSON_FAILED = 'Could not add that person. Try again, or add them in Notes.';
export const WHOSE_HEAD_WARN = "Pick this scene's POV character, or choose All";
export const WHOSE_HEAD_WRITE_ANYWAY = 'Write anyway';
export const WHOSE_HEAD_DRAFT_ANYWAY = 'Draft anyway';
export const WHOSE_HEAD_GO_BACK = 'Go back';
// Post-beta 11: an unset scene offers the last set scene's POV character.
export const WHOSE_HEAD_USE_PREVIOUS = (name, sceneName) => (
  sceneName ? `Use ${name}, as in “${sceneName}”` : `Use ${name}, as in the last scene`
);
export const WRITE_NEED_SCENE = 'Choose a scene first.';
export const WRITE_NEED_OUTLINE = 'You need an outline scene first.';
export const SCENE_WRITE_NO_SCENES = 'No scenes yet. Add one here, then you can write it.';
export const WRITE_NEED_INSTRUCTION = 'Add an instruction first.';
export const WRITE_NEED_FIGHT = 'Resolve each disagreement in Review context first.';
export const ACCEPT_NEED_DRAFT = 'Write a draft first.';
export const ACCEPT_ALREADY = 'This draft is already accepted.';
export const DRAFT_NEED_SCENE = 'This cell is not a scene yet.';

export function journeyAccessibleName(plain, product) {
  if (plain && product) return `${plain} (${product})`;
  return product || plain || '';
}

// ── Docs (upload pile) ───────────────────────────────────────────────

export const OPEN_DOCS = 'Open Docs';
export const UPLOAD_DOCS = 'Upload docs';
export const DOC_FILES = 'Uploaded files';
export const LOADING_DOCS = 'Loading docs…';
export const LOAD_DOCS_ERROR = 'Could not load docs.';

export const DOCS_LEDE =
  'Upload files you already have. They sit with the chat. They won\'t write the notes for you.';

export const DOCS_EMPTY =
  'No docs yet. Upload ones you already have.';

export const DOCS_OFFER =
  'Uploaded files are in this project. This sketch stays empty until you write it.';

export const DOCS_NAV_TIP = 'Add or view uploaded files';

export const DOCS_UPLOAD_TIP =
  'Add external files to Docs for this project, which informs notes and outline later';

export const IMPORT_EXISTING_DESC =
  'Upload or paste docs, outlines, transcripts, or a draft';

export const GET_IDEAS_DOCS_LEDE =
  'You can include series chats and uploaded files. You don\'t have to.';

export const FLAT_DOC_FORMAT = 'Loose pages';

// ── Docs — bring a story from another chat (#1051) ───────────────────
// UI chrome only. Clipboard prompt bodies live in docsOutsideChatPrompts.js
// so the markdown heading `# Story sheet` never appears in Docs chrome.
// The upload hint names the declared format the writer picks (#1172).

export const BRING_STORY_FROM_ANOTHER_CHAT = 'Bring a story from another chat';

export const COPY_OUTSIDE_CHAT_PROMPT = 'Copy outside-chat prompt';

export const COPY_PROMPT = 'Copy prompt';

export const BRING_STORY_LEAD = (
  'Copy one of these prompts into your other chat. Then paste the reply into Upload, or save a .md file and upload it.'
);

/** Prompt chooser heading. Sentence case; the legend CSS must not uppercase it. */
export const PROMPT_CHOOSER_LEGEND = "Where's your story right now?";

export const PROMPT_A_TITLE = 'I already know it scene by scene';

export const PROMPT_A_HELP = (
  'Use this when your other chat has walked through what happens, in order. '
  + 'The file will include a scene list we can put on Plot Grid.'
);

export const PROMPT_B_TITLE = 'I have the Framework, not a scene list yet';

export const PROMPT_B_HELP = (
  'Use this when you have people, places, and the idea, but not a scene-by-scene breakdown. '
  + 'We\u2019ll help you pick a Framework and work scenes inside Brainstormy. (Most writers start here.)'
);

export const PROMPT_AFTER_COPY = (
  'Next: Upload → Paste from chat (or choose a file). Format: Story sheet from outside chat.'
);

export const PROMPT_TRUST_NOTICE = (
  'We only trust structure from files made with this method.'
);

export const COPY_OUTSIDE_CHAT_PROMPT_DESC = (
  'Paste this into an outside chat to turn that chat into a Markdown file you can upload here. '
  + 'It only uses what you already said \u2014 it doesn\u2019t invent, and it doesn\u2019t force a Framework.'
);

export const COPY_FRAMEWORK_MAPPING_ADDON = 'Copy Framework-mapping add-on';

export const COPY_FRAMEWORK_MAPPING_ADDON_DESC = (
  'If you used Brainstormy Frameworks in your outside chat, copy this and paste it there too so the sheet can line up with those sequences. '
  + 'If you didn\u2019t use Brainstormy Frameworks in that chat, skip this and only use the outside-chat prompt.'
);

export const BRING_STORY_SHEET_UPLOAD_HINT = (
  'Next: Upload → Paste from chat (or choose a file). Format: Story sheet from outside chat.'
);

export const UPLOAD_A_FILE = 'Upload a file';

export const PASTE_FROM_CHAT = 'Paste from chat';

export const PASTE_FROM_CHAT_HELP = (
  'Paste the Markdown story sheet your other chat returned.'
);

export const PASTE_FROM_CHAT_PLACEHOLDER = (
  'Paste the full reply here. Keep the first line: came from Brainstormy prompt v2 or v3.'
);

export const PASTE_CLEAR = 'Clear';

export const PASTE_SHEET_FORMAT_LABEL = 'Format for this sheet';

export const BRING_STORY_PROMPT_ACCEPT_NOTICE = (
  'We only accept documents created using this prompt.'
);

export const PROMPT_SIGNATURE_SOFT_REJECT = (
  'This reads best when it came from Prompt A or B. Copy a prompt, paste the new reply here, or upload a new file.'
);

// ── Notes (today's Cards family) ─────────────────────────────────────

export const MAKE_A_NOTE = 'Make a note';
export const MAKE_NOTES = 'Make notes';
export const MAKE_NOTES_FROM_CHATS_AND_DOCS = 'Make notes from chats and docs';
export const MAKE_NOTES_FROM_DOCS = 'Make notes from Docs';
export const MAKE_NOTES_FROM_THESE = 'Make notes from these';
export const MAKE_NOTES_FROM_THESE_DOCS = 'Make notes from these Docs';
/** Upload dialog only. Skips making notes and opens the book. Not the Tasks room name. */
export const SKIP_AND_OPEN_MY_BOOK = 'Skip and open my book';
export const MAKE_CHARACTER_NOTES_FROM_THESE_DOCS = MAKE_NOTES_FROM_THESE_DOCS;
export const FROM_DOCS_LEDE = roomCopy('FROM_DOCS_LEDE',
  'Pick Docs already in the pile. Propose Character, Place, World, Logline, Theme, Conflict, Relationship, Subplot, Custom, Story Rules, and voice. You accept, edit, or Skip. Nothing is written until you say so.');
export const REVIEW_DOCS_CHARACTER_LEDE =
  'Notes from these Docs. Accept, edit, or Skip each. Existing notes stay beside new ones. Nothing extra is added.';
export const REVIEW_DOCS_NOTES_LEDE = REVIEW_DOCS_CHARACTER_LEDE;
export const NO_PEOPLE_IN_DOCS =
  'No people found in these Docs. Nothing was added.';
export const NO_MATCH_IN_DOCS =
  'No matching notes found in these Docs. Nothing was added.';
export const EMPTY_DOCS_FOR_NOTES =
  'These Docs have no readable text. Nothing was added.';
export const PICK_DOCS_FIRST = 'Pick at least one Doc.';
export const THIS_BOOK_ALTITUDE = 'This book';
export const SERIES_ALTITUDE = 'Series';
export const UPDATE_EXISTING_NOTE = 'Update existing';
export const MAKE_NEW_NOTE = 'Make new';
export const EXISTING_CHARACTER_NOTES =
  'Already in Notes — Skip this if it is a duplicate. Nothing merges on its own.';
export const EXISTING_PLACE_NOTES =
  'Already in Notes — Skip this if it is a duplicate. Nothing merges on its own.';
export const EXISTING_WORLD_NOTES =
  'Already in Notes — Skip this if it is a duplicate. Nothing merges on its own.';
export const ACCEPT_CHARACTER_NOTE_BOOK =
  'Accepting this adds a Character note on this book. That is the standing identity.';
export const ACCEPT_CHARACTER_NOTE_SERIES =
  'Accepting this adds a Character note on the series. That is the standing identity.';
export const ACCEPT_PLACE_NOTE_BOOK =
  'Accepting this adds a Place note on this book. That is the standing identity.';
export const ACCEPT_PLACE_NOTE_SERIES =
  'Accepting this adds the place on the series World note. Named places on a book are separate Place notes.';
export const ACCEPT_WORLD_NOTE_BOOK =
  'Accepting this adds the World note on this book. Places this book names are separate Place notes.';
export const ACCEPT_WORLD_NOTE_SERIES =
  'Accepting this adds the World note on the series. Places a book names are separate Place notes.';
export const ACCEPT_LOGLINE_NOTE_BOOK =
  'Accepting this adds the Logline note on this book.';
export const ACCEPT_THEME_NOTE_BOOK =
  'Accepting this adds the Theme note on this book.';
export const ACCEPT_CONFLICT_NOTE_BOOK =
  'Accepting this adds the Conflict note on this book.';
export const ACCEPT_RELATIONSHIP_NOTE_BOOK =
  'Accepting this adds a Relationship note on this book.';
export const ACCEPT_SUBPLOT_NOTE_BOOK =
  'Accepting this adds a Subplot note on this book.';
export const ACCEPT_CUSTOM_NOTE_BOOK =
  'Accepting this adds a Custom note on this book.';
export const ACCEPT_CUSTOM_NOTE_SERIES =
  'Accepting this adds a Custom note on the series.';
export const ACCEPT_STORY_RULE_BOOK = roomCopy('ACCEPT_STORY_RULE_BOOK',
  'Accepting this adds a Story Rule on this book. A hard rule that must stay true. It is not the book voice.');
export const ACCEPT_VOICE_STYLE_BOOK = roomCopy('ACCEPT_VOICE_STYLE_BOOK',
  'Accepting this saves a voice on Style Notes. It is saved, not set as the book default.');
/** Post-upload next door. Side-nav Tasks plus Make notes. */
export const IMPORT_NEXT_DOOR = `${TASKS} \u00b7 ${MAKE_NOTES}`;
/** General line after approval, when no Prompt A/B/legacy/manuscript door applies. */
export const DIGEST_SOFT_OFFER =
  "We've read your docs. Next, make notes from them, or skip and open your book.";

export const DIGEST_SOFT_OFFER_A =
  `Digest approved. ${IMPORT_NEXT_DOOR}. We found a scene list. You can put these scenes on Plot Grid, or pick a Framework first and place them under its sequences later (you map them).`;

export const DIGEST_SOFT_OFFER_A_LANDED =
  `Digest approved. ${IMPORT_NEXT_DOOR}. These scenes are on Plot Grid. Settle what each one does there.`;

export const OPEN_PLOT_TRACK = 'Open Plot Grid';

export const SCENES_ON_PLOT_TRACK = 'These scenes are on Plot Grid.';

export const DIGEST_SOFT_OFFER_B =
  `Digest approved. ${IMPORT_NEXT_DOOR}. This reads as story notes and a Framework, not a scene list. Next: pick a Framework, then work scenes in Plot Focus \u2014 same path as starting inside Brainstormy.`;

export const DIGEST_SOFT_OFFER_LEGACY =
  `Digest approved. ${IMPORT_NEXT_DOOR}. This file used an older prompt. Copy Prompt A or B and regenerate if you want the new doors.`;

/** Digest lede after approval. Path kind comes from the stamp, not the body. */
/**
 * Manuscript uploads do not get the general post-approval line. Stamped
 * Prompt A/B still uses its own door.
 */
export const DIGEST_SOFT_OFFER_MANUSCRIPT =
  "We've read your chapters. Next, make notes from them, or skip and open your book.";

export function digestSoftOfferForFiles(files, { landed = false } = {}) {
  const list = files || [];
  if (list.some((file) => file?.prompt_path_kind === 'A')) {
    return landed ? DIGEST_SOFT_OFFER_A_LANDED : DIGEST_SOFT_OFFER_A;
  }
  if (list.some((file) => file?.prompt_path_kind === 'B')) return DIGEST_SOFT_OFFER_B;
  if (list.some((file) => (
    file?.prompt_signature === 'accepted' && String(file?.prompt_version) === '1'
  ))) {
    return DIGEST_SOFT_OFFER_LEGACY;
  }
  if (list.some((file) => file?.declared_format === 'manuscript_draft')) {
    return DIGEST_SOFT_OFFER_MANUSCRIPT;
  }
  return DIGEST_SOFT_OFFER;
}

/** Confirm screen for a new upload (#2193). Wording approved 2026-10-22. */
export const PROSE_CHECK_STEP_READING = 'Reading your file\u2026';
export const PROSE_CHECK_STEP_SORTING = 'Finding your chapters\u2026';
export const PROSE_CHECK_TITLE = "Here's what we found";
export const PROSE_CHECK_OUTLINE_STAYS =
  "Brainstormy can still read sections marked Outline or Not sure, but they won't become scenes yet.";
export const PROSE_CHECK_SAVE_ONLY = 'Just save the file';
export const PROSE_CHECK_KIND_PROSE = 'Chapter';
export const PROSE_CHECK_KIND_OUTLINE = 'Outline';
export const PROSE_CHECK_KIND_UNSURE = 'Not sure';
export const PROSE_CHECK_PART = 'This heading will become a sequence on your Plot Grid.';
export const PROSE_CHECK_BACK = 'Back';
export const PROSE_CHECK_NO_BOOK =
  'Scenes need a book to go in. You can still save the file.';
export const PROSE_CHECK_COULD_NOT_SORT =
  "We couldn't find the chapters in this file. Try again, or just save the file.";
export const PROSE_CHECK_COULD_NOT_CREATE =
  "We couldn't make scenes from all of these chapters. Check your Plot Grid before you try again.";
export const PROSE_CHECK_NEED_CHAPTER =
  'Mark at least one section as Chapter to add scenes.';
/** Shown when an upload has no text. Same sentence the preview already uses. */
export const UPLOAD_FILE_EMPTY = 'File is empty.';

/** Confirm button. Count is sections marked Chapter after flips. */
export function proseCheckAddLabel(count) {
  const n = Math.max(0, Math.trunc(Number(count) || 0));
  return `Add ${n} ${n === 1 ? 'scene' : 'scenes'}`;
}

/**
 * Count line when scene breaks sit inside chapters.
 * Approved 2026-10-23: "[n] scenes in [c] chapters".
 * One scene is "1 scene". One chapter is "1 chapter".
 */
export const PROSE_CHECK_CHAPTERS_SCENES = '[n] scenes in [c] chapters';

/** Fill the approved chapters/scenes pattern, including singular forms. */
export function proseCheckChaptersScenesLine(scenes, chapters) {
  const sceneCount = Math.max(0, Math.trunc(Number(scenes) || 0));
  const chapterCount = Math.max(0, Math.trunc(Number(chapters) || 0));
  const sceneLabel = sceneCount === 1 ? '1 scene' : `${sceneCount} scenes`;
  const chapterLabel = chapterCount === 1 ? '1 chapter' : `${chapterCount} chapters`;
  return PROSE_CHECK_CHAPTERS_SCENES
    .replace('[n] scenes', sceneLabel)
    .replace('[c] chapters', chapterLabel);
}

/** Drop any count that is 0. A screen with nothing to count stays blank. */
export function proseCheckSummary({
  prose = 0,
  outline = 0,
  unsure = 0,
  chapters = 0,
  scenes = 0,
  split = false,
} = {}) {
  const proseCount = Math.max(0, Math.trunc(Number(prose) || 0));
  const outlineCount = Math.max(0, Math.trunc(Number(outline) || 0));
  const unsureCount = Math.max(0, Math.trunc(Number(unsure) || 0));
  const chapterCount = Math.max(0, Math.trunc(Number(chapters) || 0));
  const sceneCount = Math.max(0, Math.trunc(Number(scenes) || 0));
  const parts = [];
  // Scene-break line only when pieces outnumber the chapters they came from.
  if (split && chapterCount > 0 && sceneCount > chapterCount) {
    parts.push(proseCheckChaptersScenesLine(sceneCount, chapterCount));
  } else if (proseCount) {
    parts.push(proseCount === 1 ? '1 chapter' : `${proseCount} chapters`);
  }
  if (outlineCount) {
    parts.push(outlineCount === 1 ? '1 outline section' : `${outlineCount} outline sections`);
  }
  if (unsureCount) {
    parts.push(unsureCount === 1 ? '1 not sure' : `${unsureCount} not sure`);
  }
  if (!parts.length) return '';
  return `${parts.join(', ')}.`;
}

export const SCENE_LIST_KEEP_ONE = 'Keep at least one scene from the list.';

export const PUT_SCENES_ON_PLOT_TRACK = 'Put these scenes on Plot Grid';

export const ADD_SCENES_TO_PLOT_TRACK = 'Add these scenes to Plot Grid';

export const PICK_A_FRAMEWORK = 'Pick a Framework';

export const WORK_SCENES_IN_PLOT_FOCUS = 'Work scenes in Plot Focus';

export const UPLOAD_PATH_B_NEXT = (
  'This reads as story notes and a Framework, not a scene list. Next: pick a Framework, then work scenes in Plot Focus \u2014 same path as starting inside Brainstormy.'
);

export const UPLOAD_PATH_A_NEXT = (
  'We found a scene list. You can put these scenes on Plot Grid, or pick a Framework first and place them under its sequences later (you map them).'
);

export const UPLOAD_PATH_LEGACY = (
  'This file used an older prompt. Copy Prompt A or B and regenerate if you want the new doors.'
);

export const UPLOAD_PATH_NO_SCENES = (
  'Prompt A did not include a scene list, so nothing was added to Plot Grid.'
);

export const GAP_PURPOSE_BANNER = (
  'A few scenes still need what they do — start with the first one.'
);

export const GAP_PRESSURE_A = (
  'People and pressure are still thin — fix anytime in Notes.'
);

export const GAP_PRESSURE_B = (
  'Pressure is still thin — say more in chat or Notes when you can.'
);

export const GAP_PURPOSE_FIELD = 'What does this scene do in the story?';

export const GAP_PURPOSE_BLOCK = 'Add what this scene does before you draft it.';

export const GAP_SAVE_PURPOSE = 'Save what this scene does';

export const GAP_SCENE_NAME = 'Scene name';

export const GAP_SAVE_NAME = 'Save name';

export const GAP_SHOW_SCENE = 'Show that scene';

export const GAP_STILL_THIN = 'Still thin';

export const GAP_MORE = 'More';

export const GAP_FEWER = 'Fewer';

export const GAP_CHIP_WANT = 'Who wants what?';

export const GAP_CHIP_OPPOSITION = "What's in the way?";

export const GAP_CHIP_STAKES = "What's at risk if this fails?";

export const GAP_OPEN_NOTES = 'Open Notes';

export const IMPORT_SCENES_NEED_BOOK = (
  'Open a book before these scenes can go on Plot Grid.'
);

export const PLACE_SCENES_HEADING = 'Place scenes under sequences';

export const PLACE_SCENES_LEDE = (
  'These scenes are on Plot Grid. They are not on a Framework sequence yet. '
  + 'Pick a sequence for each one you want to move. Leave the rest where they are.'
);

export const ADD_FRAMEWORK_SEQUENCES = 'Add Framework sequences';

export const PLACE_UNDER_SEQUENCE = 'Place under sequence';

export const PICK_FRAMEWORK_BEFORE_MAP = (
  'Pick a Framework before placing these scenes under its sequences.'
);

export const USE_FRAMEWORK_AS_PLAN = 'Use this Framework as the plan';

export const USE_FRAMEWORK_AS_PLAN_HELP = (
  'After this, Generate waits for a solid sequence fill. Your scenes stay on Plot Grid. You place them under sequences yourself.'
);

export const KEEP_AS_PLAN_READY = (
  'This scene list is the plan. Generate does not wait for a Framework sequence fill unless you choose to use a Framework as the plan.'
);
export const STORY_ORDER_SPINE_HEADING = 'Story order';
export const STORY_ORDER_SPINE_LEDE =
  'These Docs list story order. Each scene stays its own sequence on a Framework. Beginning, Middle, and End only group them. Nothing here is invented.';
export const STORY_ORDER_SPINE_NOT_READY =
  'Using this Framework does not make the book ready to generate an outline on Plot Grid. That still needs a solid sequence fill and chat in a session.';
export const STORY_ORDER_USE_SPINE = 'Use this three-part spine';
export const STORY_ORDER_EDIT_IN_BUILDER = 'Edit these scenes in Framework Builder';
export const STORY_ORDER_NEED_BOOK =
  'Open a book before this can become the book\u2019s Framework.';
export const STORY_ORDER_GAP =
  'Scenes with no purpose line stay out of this spine. Edit them in Framework Builder to fill those gaps.';
export const STORY_ORDER_OMITTED =
  'Story order has more than 30 scenes. This Framework keeps the first 30. The rest stay in the doc.';
export const STORY_ORDER_THIN =
  'These scenes have names and no purpose lines. Edit them in Framework Builder. Nothing was invented to fill the gaps.';
export const STORY_ORDER_APPLIED =
  'This is this book\u2019s Framework. Each scene is its own sequence. It is not ready to generate an outline on Plot Grid until a sequence has a solid fill and this book has chat in a session.';
export const STORY_ORDER_APPLY_ERROR = 'Could not save this Framework from the story order.';
export const STORY_ORDER_READ_ERROR = 'Could not read story order from these Docs.';
export const NOT_NOW = 'Not now';
export const NO_BOOK_FOR_CHARACTER_NOTE =
  'This series has no book yet. Accept on the series, or start a book first.';
export const NO_BOOK_FOR_NOTE = NO_BOOK_FOR_CHARACTER_NOTE;
export const LOOKS_LIKE_EXISTING_NOTE =
  'Looks like an existing Character note. Update that one, or make a new one. Nothing merges on its own.';
export const LOOKS_LIKE_EXISTING_PLACE_NOTE =
  'Looks like an existing Place note. Update that one, or make a new one. Nothing merges on its own.';
export const LOOKS_LIKE_EXISTING_WORLD_NOTE =
  'Looks like an existing World note. Update that one, or make a new one. Nothing merges on its own.';
export const LOOKS_LIKE_EXISTING_LOGLINE_NOTE =
  'Looks like an existing Logline note. Update that one, or make a new one. Nothing merges on its own.';
export const LOOKS_LIKE_EXISTING_THEME_NOTE =
  'Looks like an existing Theme note. Update that one, or make a new one. Nothing merges on its own.';
export const LOOKS_LIKE_EXISTING_CONFLICT_NOTE =
  'Looks like an existing Conflict note. Update that one, or make a new one. Nothing merges on its own.';
export const LOOKS_LIKE_EXISTING_RELATIONSHIP_NOTE =
  'Looks like an existing Relationship note. Update that one, or make a new one. Nothing merges on its own.';
export const LOOKS_LIKE_EXISTING_SUBPLOT_NOTE =
  'Looks like an existing Subplot note. Update that one, or make a new one. Nothing merges on its own.';
export const LOOKS_LIKE_EXISTING_CUSTOM_NOTE =
  'Looks like an existing Custom note. Update that one, or make a new one. Nothing merges on its own.';
export const LOOKS_LIKE_EXISTING_STORY_RULE =
  'Looks like an existing Story Rule. Update that one, or make a new one. Nothing merges on its own.';
export const LOOKS_LIKE_EXISTING_VOICE = roomCopy('LOOKS_LIKE_EXISTING_VOICE',
  'Looks like an existing voice. Update that one, or make a new one. Nothing merges on its own.');
export const EXISTING_STORY_RULES =
  'Already in Story Rules — Skip this if it is a duplicate. Nothing merges on its own.';
export const EXISTING_VOICES = roomCopy('EXISTING_VOICES',
  'Already in Style Notes — Skip this if it is a duplicate. Nothing merges on its own.');
export const PROPOSE_DOCS_CHARACTER_ERROR =
  'Could not propose notes from those Docs.';
export const PROPOSE_DOCS_NOTES_ERROR = PROPOSE_DOCS_CHARACTER_ERROR;
export const DOCS_CHARACTER_PASS_DONE = 'This draft is finished.';
export const OPEN_BOOK_NOTES = 'Open Book Notes';
export const OPEN_SERIES_NOTES = 'Open Series Notes';
export const OPEN_STORY_RULES = 'Open Story Rules';
export const OPEN_STYLE_NOTES = roomCopy('OPEN_STYLE_NOTES', 'Open Style Notes');
export const BACK_TO_SERIES_NOTES = 'Back to Series Notes';
export const BACK_TO_DOCS = 'Back to Docs';
export const ADD_TO_BOOK_NOTES = 'Add to Book Notes';
export const SAVE_TO_BOOK_NOTES = 'Save to Book Notes';
// #1417: the one bridge from a series chat to Series Notes (word for word, writer-pressed).
export const SAVE_TO_SERIES_NOTES = 'Save to series notes';
export const SAVED_TO_SERIES_NOTES = 'Saved to series notes';
export const ADDED_TO_BOOK_NOTES = 'Added to Book Notes';
export const LOADING_BOOK_NOTES = 'Loading Book Notes…';
export const LOADING_SERIES_NOTES = 'Loading Series Notes…';
export const LOADING_STYLE_NOTES = roomCopy('LOADING_STYLE_NOTES', 'Loading Style Notes…');
export const LOAD_BOOK_NOTES_ERROR = 'Failed to load Book Notes.';
export const LOAD_SERIES_NOTES_ERROR = 'Could not load Series Notes.';
export const LOAD_STYLE_NOTES_ERROR = roomCopy('LOAD_STYLE_NOTES_ERROR', 'Could not load Style Notes.');
export const REQUEST_TOO_LONG = 'This is taking too long. Try again.';
export const SIGN_IN_EXPIRED = 'Your sign-in expired. Sign in again, then try once more.';
export const PROJECT_OPEN_FAILED = "Couldn't open your project.";
export const BOOK_LOAD_FAILED = "Couldn't load this book";
export const TRY_AGAIN = 'Try again';
export const SELECT_STORY_FOR_BOOK_NOTES = 'Select a story to view its Book Notes.';
export const OPEN_BOOK_FOR_STYLE_NOTES = roomCopy('OPEN_BOOK_FOR_STYLE_NOTES', 'Open a book for Style Notes');

export const NOTE_EMPTY =
  'No note yet. What you need is still in the chat.';

export const SERIES_NOTE_EMPTY =
  'No note yet. What you need is still in the series chat.';

export const NOTE_EMPTY_SHORT = 'No note yet';
export const NO_VOICE_YET = roomCopy('NO_VOICE_YET', 'No voice yet');
export const NO_VOICE_ON_SCENE = roomCopy('NO_VOICE_ON_SCENE', 'No book voice on this scene yet.');
export const VOICE_HEADING = roomCopy('VOICE_HEADING', 'Book voice');

export const CHARACTER_NOTE = 'Character note';
export const THEME_NOTE = 'Theme note';
export const WORLD_NOTE = 'World note';
export const CUSTOM_NOTE = 'Custom note';
export const PLACE_NOTE = 'Place note';
export const LOGLINE_NOTE = 'Logline note';
export const CONFLICT_NOTE = 'Conflict note';
export const RELATIONSHIP_NOTE = 'Relationship note';
export const SUBPLOT_NOTE = 'Subplot note';
export const STORY_RULES = 'Story Rules';
export const STORY_RULE = 'Story Rule';
export const VOICE_DRAFT = roomCopy('VOICE_DRAFT', 'Voice');
export const IDEA_NOTE = 'idea note';
export const ADD_IDEA_NOTE = 'Add a book';
export const ADD_A_GAP = 'Add a gap';
export const TIMELINE_EMPTY =
  'No books on the timeline yet. Add a book to sketch one.';
export const SAVE_TIMELINE_ITEM_ERROR = 'Could not save that.';
export const ADD_TIMELINE_ITEM_ERROR = 'Could not add that.';
export const ADD_CUSTOM_NOTE = '+ Add custom note';
export const CREATE_NOTE = 'Create note';
export const CREATE_NOTES = 'Create notes';
export const ADD_A_NOTE = '+ Add a note';
export const NOTE_TITLE = 'Note title';
export const NOTE_NEEDS_DESCRIPTION =
  'A note needs a description before it can be approved — generate one or write it yourself.';
export const APPROVE_NEEDS_TEXT = 'Add some text to approve this note.';
export const DELETE_NOTE_CONFIRM = 'Delete this note? You can put it back from Recently deleted.';
export const DELETE_NOTE = 'Delete';
export const CUSTOM_NOTE_NEEDS_TITLE = 'Give this custom note a title before approving it.';
export const ACCEPT_NOTE_ERROR = 'Could not accept this note.';
export const BOOK_NAME_NOTE_NEEDS_NAME = 'This idea note still needs a real book name.';
export const EMPTY_PEOPLE_UNTIL_NOTE =
  'This book has no people yet. The picker stays empty until a Character note exists.';

export function noBookNoteNamed(name) {
  return `No Book Note named "${name}" — add one in Book Notes, then regenerate again.`;
}

export const NO_CUSTOM_NOTES = 'No custom notes yet.';
export const NO_NOTES_SWITCH_GRID =
  'No notes yet — switch to Grid view to generate your first one.';

export const NOTES_NAV_TIP = "Create a quick view of your story's key elements";

export const STYLE_NOTES_NAV_TIP = roomCopy('STYLE_NOTES_NAV_TIP',
  'Set the voice for this book — how the prose should sound when you write');

export const STYLE_RULES_VOICE_LINE = roomCopy('STYLE_RULES_VOICE_LINE',
  'can turn it on or off. Voice lives on Style Notes, in another');

export const STORY_RULES_LEDE = roomCopy('STORY_RULES_LEDE', (
  'A story rule is a hard rule, not a voice. One plain sentence you '
  + 'edit. It can belong to the book, a person, or a scene, and you '
  + 'can turn it on or off. Voice lives on Style Notes, in another '
  + 'room. This is not where you write the scene.'
));

export const STORY_RULES_NAV_TIP = roomCopy('STORY_RULES_NAV_TIP',
  'Add rules this story must always follow when you generate \u2014 what\u2019s true, and how the voice should stay');

export const STYLE_ACTION_FAILED = roomCopy('STYLE_ACTION_FAILED', 'That Style Notes action failed.');
export const SELECT_BOOK_FOR_STYLE_NOTE = roomCopy('SELECT_BOOK_FOR_STYLE_NOTE', 'Select a book to draft a Style Note.');
export const STYLE_LEDE = roomCopy('STYLE_LEDE', (
  'A Style Note is the voice. It is one instruction you can read '
  + 'and edit. Draft it from sample lines, how the sentences work, '
  + 'the words we use, and the tone. Facts about people and world '
  + 'stay on Book Notes. The POV character is set here, not as part of '
  + 'the voice.'
));
export const VOICE_NAME = roomCopy('VOICE_NAME', 'Voice name');
export const SAVED_VOICES = roomCopy('SAVED_VOICES', 'Saved voices');
export const NO_VOICES_IN_LIBRARY = roomCopy('NO_VOICES_IN_LIBRARY',
  'No voices in the library yet. Save a voice, then save to library.');
export const POINT_SCENE_AT_VOICE = roomCopy('POINT_SCENE_AT_VOICE', 'Point this scene at this voice');
export const POINT_CHAPTER_HINT = roomCopy('POINT_CHAPTER_HINT', (
  'Point a chapter or scene at a voice here. Picking one replaces '
  + 'the whole voice. Writing the scene happens in Scene writing.'
));
export const GENERATE_VOICE_HINT = roomCopy('GENERATE_VOICE_HINT', (
  'This book only. One voice instruction from who they are and how '
  + 'they come across. POV stays separate. Accept saves the '
  + 'voice for this person — it is not the book default until you '
  + 'choose that.'
));
export const SCENE_WRITE_PLOTTRACK_HINT = (
  'Story decisions come from this scene\'s Plot Grid cells, grouped by '
  + 'main storyline. Row order is not beat order. Edit main storylines on '
  + 'Plot Grid. This room writes the scene from one instruction.'
);
export const SCENE_CONTEXT_HEADING = 'What Write will send';
export const SCENE_CONTEXT_LEDE = (
  'Read-only. Plot Grid for what happens. Book Notes as standing '
  + 'reference, not a second plot. Empty Book Notes do not block Write. '
  + 'What this point of view knows stays separate from what must stay secret.'
);
export const KNOWLEDGE_WALLS_HEADING = 'What this point of view knows';
export const MUST_STAY_SECRET_HEADING = 'Must stay secret';
export const MUST_STAY_SECRET_HINT = (
  'These are do-nots, not scene facts. The private fact itself stays off this list.'
);
export const MUST_STAY_SECRET_EMPTY = 'No hidden secrets yet.';
export const SECRET_HALF_FEEDS_WRITE = (
  'Confirm this private half. Write this scene packs it as a do-not, not as a scene fact.'
);
export const CHEKHOV_FEEDS_SECRET = (
  'Hidden packs a do-not on Write this scene until this column is payoff.'
);
export const PRIOR_PROSE_HEADING = 'Earlier accepted scenes';
export const ENTRY_HEADING = 'Coming in (already true)';
export const EXIT_HEADING = 'Intended after this scene (planned, not yet true)';
export const SEQUENCE_BOUNDARY_LINE = (
  'This is the first scene of a new sequence. Do not assume the next morning unless the notes say so.'
);
export const MUTABLE_NOTE_CHIP = 'latest — may be from later in the book';
export const AS_OF_ENTRY_HINT = 'as of the scene before this one';
export const ACCEPT_THIS_PASS = 'Accept draft';
export const ACCEPT_DRAFT = 'Accept draft';
export const REWRITE_NOTE_LABEL = 'Rewrite… (optional note)';
export const REWRITE_NOTE_PLACEHOLDER = 'Optional note, for example more tension, shorter';
export const REWRITE_DRAFT = 'Rewrite';
export const NEXT_SCENE_LABEL = 'Next scene';
export const WRITE_NEW_DRAFT = 'Write a new draft';
// #2027. Joel replaces these three strings. They are the only new writer-facing
// copy for the confirm that appears before a new draft on an accepted scene.
export const NEW_DRAFT_ON_ACCEPTED_TITLE =
  'A new draft makes this scene not accepted.';
export const NEW_DRAFT_ON_ACCEPTED_CANCEL =
  'Keep it accepted';
export const NEW_DRAFT_ON_ACCEPTED_GO =
  'Go ahead';
export const WRITE_MORE = 'Write more';
export const WRITE_MORE_VISIBLE = 'More';
export const NOTES_FOR_THIS_DRAFT = 'Notes for this draft';
export const CHOOSE_AI_MODEL = 'Choose AI model';
export const PASS_IS_DRAFT = 'This is a draft until you Accept.';
export const PASS_ACCEPTED = 'This draft is accepted.';
export const REVISE_THIS_PASS = 'Revise this draft';
// #2265. Joel confirms these three before they go live.
export const SCENE_DRAFT_LABEL = 'Scene draft';
export const COPY_SCENE_DRAFT = 'Copy';
export const COPIED_SCENE_DRAFT = 'Copied';
export const REVISE_THIS_PASS_HINT = (
  'Change the draft here. Edits are saved as you go. Accept draft saves your words, then offers Note updates.'
);
export const EDIT_ACCEPTED_SCENE = 'Edit';
export const STOP_EDITING = 'Stop editing';
export const ACCEPTED_SCENE_EDIT_HINT = (
  'Change this accepted scene here. Edits are saved as you go. Notes stay as they are.'
);
export const RESTORE_ACCEPTED_VERSION = 'Restore accepted version';
export const RESTORE_ACCEPTED_CONFIRM = 'Put back the version you accepted?';
export const RESTORE_ACCEPTED_PUT_BACK = 'Put back';
export const RESTORE_ACCEPTED_KEEP = 'Keep my edits';
export const SHRINK_ACCEPTED_SCENE = (
  'This will replace most of your accepted scene. Save anyway?'
);
export const SHRINK_ACCEPTED_SAVE = 'Save';
export const EDITED_SINCE_NOTES = 'Edited since notes were updated';
export const DRAFT_SAVING = 'Saving…';
export const DRAFT_SAVED = 'Saved';
export const DRAFT_SAVE_FAILED = "Couldn't save this draft. Your words are still here.";
export const DRAFT_SAVE_RETRY = 'Retry';
export const DRAFT_LEAVE_WARNING = 'This draft has not been saved yet.';
export const UNSAVED_CHANGES_WARNING = 'These changes have not been saved yet.';
export const UNSAVED_CLOSE_CHOICE = 'Keep editing, or discard what you typed.';
export const KEEP_EDITING = 'Keep editing';
export const DISCARD_UNSAVED = 'Discard';
export const SHOW_SCENE_CONTEXT = 'Show scene context';
export const HIDE_SCENE_CONTEXT = 'Hide scene context';
export const PASS_STOPPED_EARLY = (
  'This draft stopped before the scene ended. Continue this draft to add the rest, or Accept if this is enough.'
);
export const CONTINUE_THIS_DRAFT = 'Continue this draft';
export const CONTINUE_VS_NEW_DRAFT = (
  'Continue this draft adds to these words. Write draft starts a new draft.'
);
export const PROPOSED_NOTE_UPDATES = 'Proposed Note updates';
export const PENDING_OFFERS = 'Offered, not saved yet';
export const NOTE_OFFERS_LEDE = (
  'Short facts from this scene. Accept adds one to the Note. '
  + 'Edit it first, or Skip. Nothing is written until you do.'
);
export const ONE_NOTE_CHANGE = 'One fact. The rest of this Note stays.';
export const ACCEPT_NOTE_UPDATE = 'Accept';
export const EDIT_NOTE_UPDATE = 'Edit';
export const SKIP_NOTE_UPDATE = 'Skip';
export const SAVE_EDITED_NOTE_UPDATE = 'Save this update';
export const CANCEL_EDIT_NOTE_UPDATE = 'Cancel edit';
export const STANDING_IDENTITY_STAYS = (
  'Accept adds this fact. The rest of the Note stays.'
);
export const ADD_TO_THE_NOTE = 'Add to the Note';
export const STAYS_ON_THE_NOTE = 'Stays on the Note';
export const THIS_LINE_CHANGES = 'This line changes';
export const LINE_BECOMES = 'Becomes';
export const EDIT_THIS_FACT = 'Edit this fact';
export const CURRENT_STATE_AFTER_SCENE = 'Current state after this scene';
export const NOW_ON_THE_NOTE = 'Now';
export const PROPOSED_CURRENT_STATE = 'Proposed';
export const ROW_ORDER_NOT_BEAT_ORDER = (
  'Row order is not beat order. Do not play these top to bottom unless the notes say so.'
);
export const SCENE_WRITING_NAV_TIP = (
  'Write this scene from Plot Grid decisions, plus Book Notes when they exist'
);

/** Chat chrome (#1227). Talk is not the book. Scene writing is. */
export const BRAINSTORMING_ONLY = 'Just chatting';
export const SAVED_TO_STORY = 'This will be saved to your story.';

/**
 * Plain status for the current writing room.
 * `brainstorm` — Discovery, Explore, Focus, and other talk.
 * `scene-writing` — prose that becomes the book when you Accept.
 */
export function chatPersistenceLine(mode) {
  if (mode === 'scene-writing') return SAVED_TO_STORY;
  return BRAINSTORMING_ONLY;
}
export const PICK_ANOTHER_VOICE = roomCopy('PICK_ANOTHER_VOICE', 'Pick another voice');
export const CHOOSE_A_VOICE = roomCopy('CHOOSE_A_VOICE', 'Choose a voice');
export const USE_THIS_VOICE = roomCopy('USE_THIS_VOICE', 'Use this voice for the scene');
export const SCENE_VOICE_REPLACES = roomCopy('SCENE_VOICE_REPLACES', (
  'A scene voice replaces the whole voice. Saving for later and '
  + 'making it the book voice are separate choices. Nothing is '
  + 'added in the background.'
));
export const COPY_BOOK_VOICE_INTO_SCENE = roomCopy(
  'COPY_BOOK_VOICE_INTO_SCENE',
  'Copy the book voice into this scene',
);
export const MAKE_THIS_THE_BOOK_VOICE = roomCopy(
  'MAKE_THIS_THE_BOOK_VOICE',
  'Make this the book voice',
);
export const RULE_SITS_NEXT_TO_VOICE = roomCopy('RULE_SITS_NEXT_TO_VOICE',
  'Hard rules sit next to the voice. Off keeps the sentence.');
export const RULE_ABOUT_VOICE = roomCopy('RULE_ABOUT_VOICE', 'Rule about voice');
export const KEEP_THE_VOICE = roomCopy('KEEP_THE_VOICE', 'Keep the voice');
export const VOICE_RULE_YIELDS = roomCopy(
  'VOICE_RULE_YIELDS',
  'A rule that is itself a voice yields unless you keep it.',
);

export const BOOKS_IN_NOTES_LEDE =
  'Same books as the Timeline. Here they\'re notes.';

export const START_NOTE_LINE =
  'This note is a starting point only. Later changes to the book will not write back here.';

export const OPTIONAL_IDEA_NOTE =
  'Optional idea note — accept it onto the Timeline as an idea, edit it first, or ignore. It does not start a book.';

export const OPTIONAL_IDEA_NOTE_LABEL = 'Optional idea note';

export const ACCEPT_IDEA_NOTE_ERROR = 'Could not accept this idea note.';

export const NAME_THIS_NOTE = 'Name this note';
export const GIVE_NOTE_A_NAME = 'Give this note a name.';
export const SAVE_NOTE_ERROR = 'Could not save this note.';
export const CHANGE_LOG = 'Change log';
export const REDRAFT = 'Redraft';
export const REDRAFT_HOVER = "Brainstormy rewrites this Note from what's in your book now. You choose whether to keep it.";
export const redraftCostLine = (cost) => (
  `Redrafting this Note costs about ${cost}. Nothing changes until you accept the new version.`
);
export const REDRAFT_IT = 'Redraft it';
export const REDRAFT_CANCEL = 'Cancel';
export const REDRAFTING = 'Redrafting...';
export const REDRAFT_OFFER_HEADING = 'New version of this Note';
export const REDRAFT_OFFER_LINE = 'Your Note stays as it is until you accept this. If you accept, the old version is saved in history.';
export const REDRAFT_ACCEPT = 'Accept';
export const REDRAFT_REJECT = 'Reject';
export const REDRAFT_ACCEPTED = 'Note updated. The old version is in history.';
export const REDRAFT_REJECTED = 'Kept your Note as it was.';
// The only copy of this line. Approved by Joel, Oct 28 2026.
export const REDRAFT_NOTHING_TO_DRAW_FROM = 'Your book has no other Notes to redraft from yet, so this Note stays as it is.';
export const REDRAFT_FAILED = "Couldn't redraft this Note. Nothing changed, and you weren't charged. Try again in a moment.";
export const REDRAFT_NOT_STORED = "Couldn't redraft this Note. Nothing changed. Try again in a moment.";
export const REDRAFT_NOTE_CHANGED = 'You edited this Note while it was redrafting, so your edit stays. Redraft again for a new version.';
export const REDRAFT_COST_FAILED = "Couldn't check the cost right now. Try again in a moment.";
export const HIDE_CHANGE_LOG = 'Hide change log';
export const CHANGE_LOG_EMPTY = 'No earlier saves.';
export const CHANGE_LOG_ERROR = 'Could not open the change log.';
export const MERGE_NOTES_ERROR = 'Could not merge those notes.';
export const PROPOSE_NOTES_ERROR = 'Could not propose notes from those sources.';
export const THIN_NOTE_PLACEHOLDER =
  'Optional. Same note, one save. Leave blank for a thin note.';
export const SINGLETON_NOTE_PLACEHOLDER = 'Leave blank for a singleton note';
export const SERIES_NOTE_BODY_PLACEHOLDER =
  'A compact statement. What you need is still in the series chat until you write this.';

export const THEME_NOTE_REQUIRED = 'Theme note required';
export const THEME_NOTE_REQUIRED_BODY = (
  'Logline requires a Theme note first — the logline is only meaningful as the concrete expression of an underlying theme. No Theme note exists yet for this project.\n\n'
  + 'Clicking Generate will create both: a Theme note first, then the Logline note built from it.'
);

export const GENERATE_VOICE_FROM_CHARACTER_NOTE = roomCopy(
  'GENERATE_VOICE_FROM_CHARACTER_NOTE',
  'Generate voice from a Character note',
);
export const GENERATE_VOICE_FROM_THEIR_CHARACTER_NOTE = roomCopy(
  'GENERATE_VOICE_FROM_THEIR_CHARACTER_NOTE',
  'Generate voice from their Character note',
);
export const NO_CONFIRMED_CHARACTER_NOTES =
  'No confirmed Character notes on this book yet.';
export const PICK_CHARACTER_NOTE =
  'Pick a person with a confirmed Character note.';
export const THIN_CHARACTER_NOTE = roomCopy('THIN_CHARACTER_NOTE',
  'This Character note is thin. The draft says so and does not invent a full stranger voice. Accept, edit, or reject.');
export const UNTITLED_VOICE = roomCopy('UNTITLED_VOICE', 'Untitled voice');
export const VOICE_SAVED_FOR_BOOK = roomCopy(
  'VOICE_SAVED_FOR_BOOK',
  'Voice saved for this book. Not applied as the book default.',
);
export const NO_DEFAULT_VOICE_YET = roomCopy(
  'NO_DEFAULT_VOICE_YET',
  'This book has no default voice yet.',
);
export const REVIEW_THIS_VOICE = roomCopy(
  'REVIEW_THIS_VOICE',
  'Review this voice. Accept, edit, or reject. It is not applied as the book default.',
);
export const VOICE_INSTRUCTION_PLACEHOLDER = roomCopy(
  'VOICE_INSTRUCTION_PLACEHOLDER',
  'Draft or write the voice the writing will follow.',
);
export const VOICE_TIED_TO_PERSON = roomCopy('VOICE_TIED_TO_PERSON', 'Voice tied to a person');
export const NONE_BOOK_VOICE = roomCopy('NONE_BOOK_VOICE', 'None — book voice');
export const GIVE_VOICE_ITS_TEXT = roomCopy(
  'GIVE_VOICE_ITS_TEXT',
  'Give this voice its text before you accept it.',
);
export const WHERE_VOICE_LANDS = roomCopy('WHERE_VOICE_LANDS', 'Where this voice lands');
export const VOICE_ALREADY_HERE = roomCopy(
  'VOICE_ALREADY_HERE',
  'This voice is already in Style Notes',
);
export const PARENT_VOICE_LINE = roomCopy('PARENT_VOICE_LINE', 'Started from the parent voice');
export const NO_VOICE_SAVED_YET = roomCopy(
  'NO_VOICE_SAVED_YET',
  'No voice saved yet. Write the instruction, then Save instruction.',
);
export const EXTRA_GUIDANCE_HINT = roomCopy('EXTRA_GUIDANCE_HINT', (
  'Write follows What Write will use, above. These notes stay saved '
  + 'with the voice. They help when you draft a new instruction.'
));

/**
 * A saved placeholder is not a name. Today's placeholder is "Untitled voice".
 * With the style room on, "Untitled style" is the same kind of placeholder,
 * and a card already named "Untitled voice" still counts.
 */
export function isPlaceholderStyleName(name) {
  const text = String(name || '').trim().toLowerCase();
  if (!text) return true;
  const current = String(UNTITLED_VOICE).toLowerCase();
  if (text === current) return true;
  if (current !== 'untitled voice' && text === 'untitled voice') return true;
  return false;
}

export const FROM_CHATS_AND_DOCS_LEDE =
  'Pick which chats and docs to use. Nothing is saved until you say yes to one.';

export const REVIEW_NOTES_LEDE =
  'Accept or skip each offer. Existing notes stay next to new ones so you can reject a duplicate. Nothing merges on its own.';

export const BOOK_ACCEPT_NOTE =
  'Accepting this adds an idea note on Timeline. The same book shows in Series Notes. It does not start a book.';

export const CHARACTER_NOTE_SKETCH =
  'Note — a compact sketch on Series Notes.';

export const CHARACTER_DOSSIER_NOTE_LINE =
  'Longer draft — lives on this Series Note. Open it from the note. Sketch language. No invented ending.';

export const CUSTOM_ACCEPT_NOTE =
  'Accepting this adds a custom note on Series Notes.';

export const PLACE_ACCEPT_NOTE =
  'Accepting this adds the place snapshot on Series Notes.';

export const PLOT_ACCEPT_NOTE =
  'Accepting this adds a plot snapshot on Series Notes.';

export const BRING_LEDE = (
  'Standing series notes that can live in this book too. Accept copies them here as series planning, then this book stays empty until you fill it. Skip leaves no book note.'
);

export const BRING_DEST_NOTES = {
  people: 'Accepting this adds a Character note. The series line stays marked as series planning.',
  world: 'Accepting this adds the World note for this book. Places this book names are separate Place notes.',
  custom: 'Accepting this adds a custom note. It does not fill Plot Dossier or Plot Grid.',
  theme: 'This is series context. It does not write Book Theme.',
};

export const BRING_EMPTY_LIST = (
  'No standing series notes to bring in. Make notes on the series first, or start with this book’s own chat.'
);

// ── Companion copy (chat turns are not Notes) ────────────────────────

export function sessionMessageSummary(count) {
  if (count === 1) return '1 message in this session.';
  return `${count} messages in this session.`;
}

export const PLOTTRACK_STANDING_DISCLAIMER = (
  'This outline came from the chat so far: sessions, Plot Workshop, people, '
  + 'and world. Look it over. It may put scenes in the wrong place.'
);

export const PLOTTRACK_REPLACE_STAYS =
  'Sessions, Plot Workshop, people notes, and world notes stay.';

export const PLOTTRACK_EMPTY_GENERATE = (
  "This book's outline stays empty until you chat in this book or "
  + 'upload docs into this book, then Generate. Not from series chats. '
  + 'Not from the series board. Docs from the series are not enough. '
  + 'Docs on this book are enough.'
);

export const PLOTTRACK_READING =
  'Reading the chats and docs for this book…';

export const PLOTTRACK_USE_NOTES = 'Use Notes for Generate';
export const PLOTTRACK_MODE_TALK_DOCS = 'Using chat and Docs.';
export const PLOTTRACK_MODE_STEER =
  'Using Framework + Book Notes (chat and Docs fill gaps).';
export const PLOTTRACK_MODE_FILLS = 'Using Framework sequences and chat.';
export const PLOTTRACK_MODE_FILLS_ONLY = 'Using Framework sequences.';
export const PLOTTRACK_OUTLINE_LOADING = 'Opening this book’s outline…';
export const PLOTTRACK_THIN_WARN = (
  'This book has little in chat, Docs, or confirmed Workshop threads. '
  + 'Generate will invent from thin material until it has a main character, '
  + "what they want, and what's in the way. Put those in this book's chat or in Notes."
);
// #2073: the bar is a main character, a want, and an obstacle.
export const PLOT_WORKSHOP_GENERATE_BLOCKED = (
  'Generate opens once this book has its main character, what they want, '
  + "and what's in the way. A genre Framework is optional."
);
/** #1472: the pre-outline gate is the wrong next step once scenes are on the outline. */
export const OUTLINE_EXISTS_LEAD = 'This book already has an outline.';
export const OUTLINE_EXISTS_NEXT_STEP = (
  `${OUTLINE_EXISTS_LEAD} Next: draft the next scene in Scene Writing, `
  + 'or review a draft that is not accepted yet.'
);
export const PLOT_WORKSHOP_SEE_GAPS = 'Open Explore your story for the next step.';
/** #1326: the real Generate gate as steps with a door each. */
export const READY_STEPS_LABEL = 'To open Generate';
export const READY_STEP_DONE = 'Done';
export const READY_STEP_TO_DO = 'To do';
export const READY_STEP_DOORS = {
  framework: 'Open Plot Workshop',
  sequence: 'Open Plot Workshop',
  talk: 'Open Explore your story',
};
/** Run 46: with a framework, a worked sequence is the other way to open Generate. */
export const READY_STEP_OR_SEQUENCE_DOOR = 'Or work a sequence in Plot Workshop';
export const PLOTTRACK_THIN_WORKSHOP = (
  ' If the hole is a Framework or a main storyline, open Workshop.'
);
// Run 58: a plan note opens Generate with this ticked (#1665 follow-up).
export const PLOTTRACK_NOTES_HANDY = (
  'Tick this and your Book Notes guide the outline. A plan note counts when it names the main character, what they want, and what\'s in the way.'
);
export const PLOTTRACK_NO_FRAMEWORK_STEER = (
  'No Framework is chosen. Generate will use chat and Docs. Notes stay in '
  + 'the background. Choose a Framework to use Notes to steer.'
);
export const PLOTTRACK_OFFER_ACCEPT = 'Accept';
export const PLOTTRACK_OFFER_EDIT = 'Edit';
export const PLOTTRACK_OFFER_REJECT = 'Reject';
export const PLOTTRACK_FILL_EMPTY_HINT = 'Accept fills empty spots only.';
// #1343: say what each choice does to the writer's outline.
export const PLOTTRACK_REPLACE_EXISTING = 'Replace my whole outline…';
export const PLOTTRACK_FIGHT = (
  'This Note and the chat disagree. Keep the Note, keep the chat, or edit.'
);
export const PLOTTRACK_KEEP_NOTE = 'Keep Note';
export const PLOTTRACK_KEEP_TALK = 'Keep the chat';
export const PLOTTRACK_FIGHT_EDIT = 'Edit';

// #1105 / #1106 generation integrity
export const PLOTTRACK_ENTITY_BLEED = (
  "Many names and places in this outline are not in this book's chat or Notes. "
  + 'Look it over before you Accept. Prefer names from your chat.'
);
export const PLOTTRACK_INCOMPLETE_OUTLINE = (
  'This outline is not finished yet. The ending or last act still needs scenes. '
  + 'Use Continue to fill what is missing — do not treat this as done.'
);
export const PLOTTRACK_CONTINUE_INCOMPLETE = 'Continue incomplete outline';
export const PLOTTRACK_ACCEPT_ANYWAY = 'Add to my outline anyway';
export const PLOTTRACK_FINISHED_INCOMPLETE = (
  'Outline saved, but the ending is still incomplete. Use Continue to finish it.'
);


export const ADD_THROUGH_LINE = 'Add a main storyline';
/** Run 36: add a scene on Plot Grid without a framework. */
export const SCENE_MENU = (name) => `Scene menu for ${name}`;
export const SCENE_MENU_OPEN = 'Open';
export const SCENE_MENU_RENAME = 'Rename';
export const SCENE_MENU_MOVE = 'Move to';
export const SCENE_MENU_CHAPTER = 'Start a chapter here';
export const CHAPTER_REMOVE_BREAK = 'Remove break';
export const CHAPTER_REMOVE_BREAK_ARIA = (name) => `Remove break for ${name}`;
export const CHAPTER_BREAK_REMOVED = 'Chapter break removed.';
export const SCENE_MENU_CHAT = 'Chat about this scene';
export const SCENE_MENU_DELETE = 'Delete scene';
export const SCENE_MENU_DELETE_TITLE = 'Delete this scene?';
export const SCENE_MENU_DELETE_BODY = 'The scene leaves the Plot Grid. Undo puts this same scene back, including its prose and notes.';
export const UNDO_ACTION = 'Undo';
export const UNDO_DISMISS = 'Dismiss';
export const UNDO_ACCEPT = 'Undo accept';
export const RECENTLY_DELETED = 'Recently deleted';
export const RECENTLY_DELETED_EMPTY = 'Nothing from the last 30 days.';
export const RECENTLY_DELETED_PUT_BACK = 'Put back';
export const SCENE_MOVED = 'Scene moved.';
export const SCENE_DELETED = 'Scene deleted.';
export const SCENE_DELETE_NO_UNDO = 'Scene deleted. Undo puts this same scene back.';
export const SCENE_MENU_DELETE_CONFIRM = 'Delete scene';
export const SCENE_MENU_KEEP = 'Keep it';
export const SCENE_MENU_RENAME_SAVE = 'Save name';
export const ADD_SCENE = 'Add a scene';
export const ADD_SCENE_PLUS = '+ Add a scene';
export const ADD_SCENE_PART = (part) => `Add a scene to ${part}`;
export const ADD_SCENE_AFTER = 'After';
// Run 48: rename a scene or move it to another sequence from the Plot Grid.
export const SCENE_EDIT_LABEL = 'This scene';
export const SCENE_EDIT_TITLE = 'Title';
export const SCENE_EDIT_SEQUENCE = 'Sequence';
export const SCENE_EDIT_PLACE = 'Place';
export const SCENE_EDIT_PLACE_STAY = 'Where it is now';
export const SCENE_EDIT_PLACE_START = 'At the start';
export const SCENE_EDIT_PLACE_END = 'At the end';
// Run 61: with every sequence listed, eleven bare "At the start" lines read alike.
export const SCENE_EDIT_PLACE_START_OF = (sequence) => `At the start of ${sequence}`;
export const SCENE_EDIT_PLACE_AFTER = (title) => `After "${title}"`;
export const SCENE_EDIT_SAVE = 'Save scene';
export const SCENE_EDIT_NEEDS_TITLE = 'A scene needs a title.';
export const SCENE_EDIT_FAILED = 'Could not save this scene.';
export const SAVED = 'Saved.';
export const ADD_SCENE_WHAT = 'What happens';
export const ADD_SCENE_WHAT_HINT = 'A sentence or two in your own words. Drafting reads this.';
export const ADD_SCENE_TITLE = 'Title (optional)';
export const ADD_SCENE_TITLE_HINT = 'Leave it blank and the first words of what happens become the title.';
export const ADD_SCENE_SUGGEST = 'Suggest title';
export const ADD_SCENE_SUGGESTING = 'Suggesting…';
export const ADD_SCENE_SUGGEST_NEEDS_WHAT = 'Say what happens first, then ask for a title.';
export const ADD_SCENE_SAVE = 'Add scene';
export const ADD_SCENE_SAVING = 'Adding…';
export const ADD_SCENE_CANCEL = 'Cancel';
export const ADD_SCENE_NEEDS_WHAT = 'Say what happens in this scene.';
export const ADD_SCENE_ADDED = (title) => `Added "${title}".`;
export const ADD_SUBPLOT = 'Add subplot';
export const ADD_CHEKHOV = 'Add Chekhov';
export const ADD_MAIN = 'Add main';
export const SUBPLOT_KIND = 'Subplot';
export const CHEKHOV_KIND = 'Chekhov';
export const MAIN_KIND = 'Main';
export const MAIN_STORYLINE = 'Main storyline';

/** The scene panel's small row label. The stored default "Main plot" reads as the storyline. */
export function panelStorylineLabel(row) {
  const label = String(row?.label || '').trim();
  if (row?.type === 'main' && (label === '' || label === 'Main plot')) return MAIN_STORYLINE;
  return label;
}
export const THROUGH_LINE_NAME = 'Name';
export const NAME_THIS_THROUGH_LINE = 'Name this main storyline';
export const RENAME_THROUGH_LINE = 'Rename';
export const SAVE_THROUGH_LINE_NAME = 'Save name';
export const CANCEL_THROUGH_LINE = 'Cancel';
export const CHEKHOV_HOW_CLEAR = 'How clearly this is planted';
export const CHEKHOV_HIDDEN = 'Hidden';
export const CHEKHOV_HIDDEN_HINT = 'Easy to miss';
export const CHEKHOV_OPEN = 'Open';
export const CHEKHOV_OPEN_HINT = 'In the open';
export const CHEKHOV_OBVIOUS = 'Obvious';
export const CHEKHOV_OBVIOUS_HINT = 'Hard to miss';
export const CHEKHOV_SETUP_PAYOFF_HINT =
  'Setup and payoff stay on this planted row only.';
export const SUBPLOT_STATES_HINT = 'Mark this as active, quiet, blank, or absent.';
export const ABSENT_CELL_TITLE = 'Mark this cell absent?';
export const ABSENT_CELL_BODY = 'This removes the note and its moments.';
export const ABSENT_CELL_CONFIRM = 'Mark absent';
export const PLOTTRACK_NO_ROWS =
  'No main storylines yet. Add a subplot or Chekhov, or Generate from this book.';

export const SEQUENCE_RUNS_FROM = 'This sequence runs from';
export const ACT_RUNS_FROM = 'This act runs from';
export const CHAPTER_RUNS_FROM = 'This chapter runs from';
export const HEADER_RUNS_TO = 'to';
export const STARTS_AT_SCENE = 'Starts at scene';
export const ENDS_AT_SCENE = 'Ends at scene';
export const SAVE_HEADER_SPAN = 'Save';
export const CANCEL_HEADER_SPAN = 'Cancel';
export const EDIT_SEQUENCE = 'Edit this sequence';
export const EDIT_ACT = 'Edit this act';
export const EDIT_CHAPTER = 'Edit this chapter';
export const CHAPTER_SEED_LEDE = (
  'Group scenes into chapters for the reader. Suggest breaks, or end a chapter after a scene.'
);
export const SUGGEST_CHAPTER_BREAKS = 'Suggest chapter breaks';
export const END_CHAPTER_AFTER = 'End a chapter after';
export const PLACE_CHAPTER_BREAK = 'Place chapter break';
export const ACCEPT_CHAPTER_BREAK = 'Accept';
export const DISMISS_CHAPTER_BREAK = 'Dismiss';
export const SUGGESTED_CHAPTER_BREAK = 'Suggested chapter break';
export const CHAPTER_SEED_NONE = (
  'No chapter breaks suggested. End a chapter after a scene instead.'
);
export const CHAPTER_BREAK_COULD_NOT_SUGGEST = 'Could not suggest chapter breaks.';
export const CHAPTER_BREAK_COULD_NOT_PLACE = 'Could not place the chapter break.';
export const SPAN_CONTINUITY_LEDE = (
  'Later accepted scenes and Note stamps were not rewritten. Review what still fits.'
);

export function spanContinuityDetails(flags) {
  const details = [];
  const seen = new Set();
  for (const flag of flags || []) {
    const detail = String(flag?.detail || '').trim();
    if (!detail || seen.has(detail)) continue;
    seen.add(detail);
    details.push(detail);
  }
  return details;
}

export const FRAMEWORK_OPEN_BOOK_LINE =
  'Open a book to choose a Framework. Framework Builder is this room.';
export const FRAMEWORK_BUILDER_LEDE =
  'Framework Builder is where you choose a Framework for this book.';
/** Page heading on /frameworks. Locked: Framework, never structure. */
export const FRAMEWORK_BUILDER_HEADING = "This book's Framework";
/** Quiet one-line definition under the titled chrome (#1288). Sequences, per #1804. */
export const FRAMEWORK_BUILDER_DEFINITION =
  'Pick a Framework to give your story its sequences based on your genre, and Brainstormy will use it to suggest what happens in each sequence of your outline.';
export const THIS_IS_THIS_BOOKS_FRAMEWORK = "This is this book's Framework.";
export const THIS_BECOMES_THIS_BOOKS_FRAMEWORK = "This becomes this book's Framework.";
export const FRAMEWORK_BUILT_SAVE_OR_EDIT = 'Each sequence is tagged. Save it as it is, or edit in place first.';
// Runs 40–42: say it where the writer clicked, not only at the top of the page.
export const FRAMEWORK_SAVED = (name) => (name ? `Saved “${name}”.` : 'Saved.');
export const FRAMEWORK_NOW_THIS_BOOKS = (name) => `“${name}” is now this book's Framework.`;
export const FRAMEWORK_NOW_USED = (name) => `Using “${name}”.`;
export const FRAMEWORKS_NEXT =
  "Pick a Framework. That sets it as this book's Framework.";
export const FRAMEWORK_SAVE_ERROR = "Could not save this book's Framework.";
export const SESSION_FRAMEWORK_HINT =
  "A disposable brainstorming reference for this session only. It is not saved as this book's Framework.";

/** Create New Session stays in the open book (#1226). */
export const EXPLORE_MODE_NAME = 'Explore';
export const EXPLORE_MODE_SHORT = 'Recommended for a brand-new idea';
export const EXPLORE_MODE_HELP =
  'Follow one thread and see where it goes. You do not have to know the scene yet.';
export const EXPLORE_MODE_EXAMPLES =
  'A person you just thought of. A place that might matter. A what-if that is not a scene yet.';
export const FOCUS_MODE_NAME = 'Focus';
export const FOCUS_MODE_SHORT = 'Go deep on one thing';
export const FOCUS_MODE_HELP =
  "Stay with one thing you already want to develop, such as a person, the book's argument, or the world.";
export const STAYS_IN_BOOK = 'This stays in the book you have open.';
// Build with help, as a conversation (after run 39).
export const FRAMEWORK_TALK_INTRO =
  "Work it through in chat and Brainstormy will suggest sequences for your book, or write them yourself.";
export const FRAMEWORK_TALK_OPENING =
  "Let's build a Framework for your book. What kind of story is it, in a few words?";
export const FRAMEWORK_TALK_RESEARCH = (name) => `From Brainstormy's genre notes: ${name}`;
// Run 53: the reply said there were no notes for the writer's genre and drew on a close one;
// the badge read as if that were their genre.
export const FRAMEWORK_TALK_RESEARCH_NEAREST = (name) => (
  `No genre notes for this kind of book. Closest notes Brainstormy has: ${name}`
);
export const FRAMEWORK_TALK_SOURCES = 'Those notes were drawn from:';
export const FRAMEWORK_TALK_BUILD_HINT =
  'If this works, click Build. To change it, say what to change.';
export const FRAMEWORK_TALK_DISCOVERY = 'Work an idea out in Discovery';
export const FRAMEWORK_TALK_DISCOVERY_NO_BOOK =
  'Open a book, then work an idea out in its Discovery chat.';
export const FRAMEWORK_TALK_UNREACHABLE = "Couldn't reach Brainstormy. Check your connection and send again.";
export const FRAMEWORK_BUILDER_NAV_TIP =
  'Choose a Framework for this book — act names and sequences from genre conventions';

/** Writer-facing /frameworks + Framework picker chrome (#1089 leftover). */
export const FRAMEWORK_BUILDER_UI_STRINGS = [
  FRAMEWORK_BUILDER_HEADING,
  FRAMEWORK_BUILDER_DEFINITION,
  FRAMEWORK_OPEN_BOOK_LINE,
  FRAMEWORK_BUILDER_LEDE,
  THIS_IS_THIS_BOOKS_FRAMEWORK,
  THIS_BECOMES_THIS_BOOKS_FRAMEWORK,
  FRAMEWORKS_NEXT,
  FRAMEWORK_SAVE_ERROR,
  SESSION_FRAMEWORK_HINT,
  FRAMEWORK_BUILDER_NAV_TIP,
];

export const PATH_CHAT = 'Chat';

export const PATH_CHAT_OR_DOCS = 'Chat or docs';

/** Fresh-start label for the talk_or_notes row. Document-started books use PATH_CHAT_OR_DOCS. */
export const PATH_TALK_OR_DOCS = PATH_CHAT;

export const PATH_TEACH_CHAT =
  'Source material for this book. Only chat counts. Series Docs do not.';

export const PATH_TEACH_CHAT_OR_DOCS =
  'Source material for this book. Chat and uploaded docs both count. Series Docs do not.';

export const PATH_TEACH_TALK_OR_DOCS = PATH_TEACH_CHAT;

export const PATH_TEACH_PEOPLE =
  'Named people and places become notes so later steps can use them without inventing a second cast.';

export const PATH_TEACH_PLOT =
  'Plot is an ordered chain of actions with consequence. Confirmed turns are what the outline seeds from, not the raw docs pile.';

export const PATH_SERIES_DOCS_ONLY = (
  'These docs are not on this book. Plot Grid Generate for this book stays empty until this book has chat or its own docs.'
);

export const PATH_GAPS_EMPTY =
  'Nothing to ask yet. Chat or add docs, then ask again.';

export const PATH_AUDIT_TIP =
  "An audit tool that analyzes what's present and missing from your chats and/or docs";

export function noKindNotesYet(category) {
  const name = String(category || '').trim().toLowerCase() || 'custom';
  return `No ${name} notes yet.`;
}

export function createNoteLabel(count) {
  return count > 1 ? CREATE_NOTES : CREATE_NOTE;
}

export {
  BRAINSTORMY_OUTSIDE_CHAT_PROMPT_VERSION,
  BRAINSTORMY_PROMPT_STAMP_LINE,
  OUTSIDE_CHAT_PROMPT_A,
  OUTSIDE_CHAT_PROMPT_B,
  PROMPT_A_STAMP_LINE,
  PROMPT_B_STAMP_LINE,
  PROMPT_A_VERSION,
  PROMPT_B_VERSION,
  OUTSIDE_CHAT_ADDON_PROMPT,
} from './docsOutsideChatPrompts';

// Migration 183: drafts outlive their scenes (Scene Writing).
export const DETACHED_HEADING = (count) => (
  count ? `Drafts not in your outline (${count})` : 'Drafts not in your outline'
);
export const DETACHED_INTRO = (
  'These drafts lost their scene when your outline changed. Nothing was deleted. '
  + 'Attach one to a scene to keep working on it; it becomes that scene\'s latest draft.'
);
export const DETACHED_ACCEPTED = 'Accepted';
export const DETACHED_READ = 'Read';
export const DETACHED_HIDE = 'Hide';
export const DETACHED_ATTACH_LABEL = 'Attach to';
export const DETACHED_PICK = 'Choose a scene…';
export const DETACHED_ATTACH = 'Attach';
export const DETACHED_ATTACHED = (from, to) => `Attached the draft from “${from}” to “${to}”.`;

// Check my sequences (readiness + recommendations). Runs only when asked;
// advice only, and the writer can ignore any of it.
export const SEQUENCE_CHECK = 'Check my sequences';
export const SEQUENCE_CHECK_BUSY = 'Checking sequences…';
export const SEQUENCE_CHECK_FAILED = "Couldn't check your sequences. Try again.";
export const SEQUENCE_CHECK_SUMMARY = (ready, total) => (
  `${ready} of ${total} sequence${total === 1 ? '' : 's'} decided.`
);
export const SEQUENCE_CHECK_WHEN = (when) => `Last checked ${when}.`;
export const SEQUENCE_CHECK_ALL_DECIDED = 'Every sequence has something decided. Nothing to suggest.';
export const SEQUENCE_CHECK_ADVICE_NOTE = 'Suggestions only. Ignore any you disagree with.';
export const SEQUENCE_STATUS_NEEDS = 'Needs a decision';
export const SEQUENCE_STATUS_NO_TALK = 'Not discussed yet';
export const SEQUENCE_STATUS_WARN = 'Decided, one thing to look at';
export const SEQUENCE_STATUS_CHANGED = 'Changed since the check';
export const SEQUENCE_CHANGED_HINT = 'You added more about this sequence. Check again to update it.';
export const SEQUENCE_RELATED = 'Related sequence';
export const SEQUENCE_IGNORE = 'Ignore';
export const SEQUENCE_IGNORED_TOGGLE = (count) => `Ignored (${count})`;
export const SEQUENCE_SHOW_AGAIN = 'Show again';
export const SEQUENCE_SHOWN_NEXT_CHECK = 'It comes back with your next check.';
export const SEQUENCE_DECIDED_TOGGLE = (count) => `Decided (${count})`;

// Before whole-book Generate (cold-start run 8: 3 real scenes, 24 placeholders).
export const PREGEN_TITLE = 'Before you generate';
// Run 9: with people in Book Notes, Generate wrote real scenes for every
// undiscussed sequence; "placeholder scenes" scared the writer for nothing.
// Run 8 (no Book Notes) got 24 placeholders. Say which, by Book Notes.
export const PREGEN_UNTALKED = (missing, total, hasNotes = false) => (
  `${missing} of ${total} ${missing === 1 ? 'sequence has' : 'sequences have'} not been discussed. `
  + (hasNotes
    ? 'Generate will make up what happens there from your pitch and Book Notes: a draft to react to, not your decisions.'
    : 'With no people in Book Notes yet, Generate may leave them as placeholder titles.')
);
export const PREGEN_BRIEF = (count) => (
  `${count} ${count === 1 ? 'sequence was' : 'sequences were'} discussed only briefly, a sentence or two. `
  + 'Generate will guess at what happens there.'
);

// Plot Focus, beside one sequence's Generate. Advice only; Generate works either way.
export const SEQ_BRIEF = "You've only said a sentence or two about this sequence. Generate will guess at what happens.";
export const SEQ_CHECK_ONE = 'Check this sequence';
export const SEQ_CHECK_ONE_BUSY = 'Checking this sequence…';
export const SEQ_CHECK_ONE_READY = 'Decided enough to generate.';
export const SEQ_CHECK_ONE_ADVICE = 'Advice only. Generate works either way.';
export const SEQ_CHECK_ONE_FAILED = 'This sequence could not be checked. Generate still works.';
export const PREGEN_NO_NOTES = (
  'This book has no people in Book Notes yet. Saving them first keeps them in the outline '
  + 'and in every scene you draft.'
);
export const PREGEN_CHECK = 'Check my sequences first';
export const PREGEN_ANYWAY = 'Generate anyway';
// Joel 2026-10-11: the Replace confirm names the action. Generate still says "Generate anyway".
export const PREGEN_REPLACE_OUTLINE = 'Replace outline';
export const PREGEN_NOT_NOW = 'Not now';
export const OFFER_PLACEHOLDER = 'Placeholder: a title only, not from your chat';
export const OFFER_PLACEHOLDER_COUNT = (count) => (
  `${count} of these ${count === 1 ? 'is a placeholder' : 'are placeholders'}: a title only, not from your chat.`
);


// Manual Create Summary pre-flight — shown INSTEAD of the cost dialog when the
// backend would refuse (never ask the writer to pay for a refused summary).
export const SUMMARY_TOO_FEW = (min, have) => (
  `Summaries need at least ${min} messages. You have ${have}.`
);
export const SUMMARY_NOTHING_NEW = 'Nothing new to summarize since the last summary.';

// Series Timeline Reveal pin (run 11: "DEMIURGE is revealed in Book 7+").
export const REVEAL_TITLE = 'Reveal';
export const REVEAL_WHAT_LABEL = 'What is revealed';
export const REVEAL_WHAT_PLACEHOLDER = 'The secret, the fact, the name…';
export const REVEAL_WHEN_LABEL = 'When and how it comes out';
export const REVEAL_WHEN_PLACEHOLDER = 'Book 7, in a letter, at the trial…';
export const REVEAL_UNTITLED = 'Untitled reveal. Click to fill in.';

// #1417: books in a series don't read the series. Shown in an empty book Discovery chat.
// Same words as backend services/book_discovery_opener.SERIES_NOT_READ_LINE.
export const SERIES_BOOK_CHAT_NOTE =
  "This chat doesn't read your Series Notes or the series chat, so tell it what this book needs to know.";

// Saved styles (style room). New strings only. Older voice strings stay.
export const YOUR_STYLES = 'Your styles';
export const NEW_STYLE = 'New style';
export const MAKE_A_COPY = 'Make a copy';
export const BOOK_STYLE = 'Book style';
export const MAKE_THIS_THE_BOOK_STYLE = 'Make this the book style';
export const USE_THE_BOOK_STYLE = 'Use the book style';
export const STYLE_WORD = 'Style';
export const THIS_SCENE = 'This scene';
export const THIS_CHAPTER = 'This chapter';
export const STYLE_CHANGE = 'Change';
export const NO_STYLE_SET = 'No style set. Write follows the scene as planned.';
export const MAKE_A_NEW_STYLE = 'Make a new style…';
export const HOW_WRITING_SHOULD_SOUND = 'How the writing should sound';
export const SAMPLE_OF_THE_SOUND = 'Sample of the sound';
export const MOVED_NOTES_INTO_STYLE = 'We moved these notes into your style description';
export const THIS_IS_NOW_YOUR_BOOK_STYLE = 'This is now your book style.';
export const PUT_ALL_BACK_ON_BOOK_STYLE = 'Put all back on the book style';
export const OPEN_SCENE = 'Open';
export const STYLE_ROOM_SAVE = 'Save';
export const DELETE_STYLE = 'Delete';
export const STYLE_RENAME = 'Rename';
export const SCENES_BACK_ON_BOOK_STYLE = 'Those scenes are back on the book style.';
export const BACK_ON_BOOK_STYLE = "They'll go back to the book style.";
export const COPIED_FROM = (name) => `Copied from ${name}`;
export const USED_IN_SCENES = (count) => (
  count === 1 ? 'Used in 1 scene' : `Used in ${count} scenes`
);
export const USED_IN_MORE_SCENES = (count) => (
  count === 1 ? 'Used in 1 more scene' : `Used in ${count} more scenes`
);
export const STYLE_ACTIONS_FOR = (name) => `Actions for ${name}`;
// Joel approved these on 2026-10-24 (#2194). Approved #2194 names stay as written.
export const PICK_A_STARTING_SOUND = 'Pick a style to start from. You can change anything later.';
export const START_FROM_SCRATCH = 'Start from scratch';
export const START_FROM_MY_OWN_WRITING = 'Start from my own writing';
export const OWN_WRITING_LEDE = (
  'Got a page you like? Paste up to 150 words of your own writing, and Brainstormy\'s drafts will sound like it.'
);
export const USE_FOR_THIS_BOOK = 'Make this the book style';
export const STYLE_FIELD_OPTIONAL = 'Optional';
export const SAMPLE_WORD_LIMIT = (count, limit = 150) => `${count} of ${limit} words`;
export const UNSAVED_STYLE_CONFIRM = 'You haven\'t saved your changes to this style. Leave without saving?';
export const STYLE_NEEDS_SOMETHING = 'Add a description, move a dial, or paste a sample first.';
export const PASTE_A_SAMPLE_FIRST = 'Paste some of your writing first.';
export const DIAL_CLOSENESS = 'Closeness';
export const DIAL_FAR = 'Far';
export const DIAL_CLOSE = 'Close';
export const DIAL_PACE = 'Pace';
export const DIAL_FAST_FORWARD = 'Fast-forward';
export const DIAL_EVERY_MOVE = 'Every move';
export const DIAL_SENTENCE_LENGTH = 'Sentence length';
export const DIAL_SHORT = 'Short';
export const DIAL_LONG = 'Long';
export const DIAL_WORD_CHOICE = 'Word choice';
export const DIAL_EVERYDAY = 'Everyday';
export const DIAL_BOOKISH = 'Bookish';
export const WRITTEN_WITH = (styleName, modelName) => {
  if (styleName && modelName) return `Written with ${styleName} · ${modelName}`;
  if (styleName) return `Written with ${styleName}`;
  if (modelName) return `Written with ${modelName}`;
  return '';
};
